/**
 * Privé Brain — Storefront Components
 * Connects to prive-search.fly.dev for AI-powered product features.
 *
 * Components:
 *   <brain-similar-fragrances>  — Scent-profile matched recommendations
 *   <brain-fragrance-profile>   — Structured notas/familia/concentración
 *   <brain-image-search>        — Camera icon for visual product search
 *   <brain-whatsapp-inquiry>    — WhatsApp product inquiry button
 */

(function() {
  'use strict';

  var API = 'https://prive-search.fly.dev';
  var WA_NUMBER = (document.querySelector('meta[name="prive-wa"]') || {}).content || '';

  // Customer login state — checked from Shopify meta tag (set server-side)
  // and localStorage (set by prive-customer-brain.js after identify).
  function _isLoggedIn() {
    var metaCid = (document.querySelector('meta[name="prive-cid"]') || {}).content || '';
    if (metaCid) return true;
    try { return !!localStorage.getItem('prive_brain_cid'); } catch(e) { return false; }
  }

  // Per-session cache for idempotent GETs. sessionStorage scope (per tab) is
  // the right lifetime: profile/similar responses don't change mid-browse and
  // cheap caching here absorbs 429s when users revisit the same product.
  // POST /api/search/image doesn't use this helper, so it stays uncached.
  function brainFetch(path) {
    var cacheKey = 'prive_brain_cache:' + path;
    try {
      var hit = sessionStorage.getItem(cacheKey);
      if (hit) return Promise.resolve(JSON.parse(hit));
    } catch (e) {}
    return fetch(API + path).then(function(r) {
      if (!r.ok) throw new Error('Brain API ' + r.status);
      return r.json();
    }).then(function(json) {
      try { sessionStorage.setItem(cacheKey, JSON.stringify(json)); } catch (e) {}
      return json;
    });
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text) e.textContent = text;
    return e;
  }

  function svgEl(tag, attrs) {
    var e = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (var k in attrs) if (attrs.hasOwnProperty(k)) e.setAttribute(k, attrs[k]);
    return e;
  }

  function fmtPrice(p) {
    return p != null ? 'L ' + Number(p).toLocaleString('es-HN') : '';
  }

  function _intelMatchPayload(match, index) {
    match = match || {};
    var out = { rank: index + 1 };
    if (match.handle) out.handle = String(match.handle);
    if (match.product_id) out.product_id = String(match.product_id);
    if (match.variant_id) out.variant_id = String(match.variant_id);
    if (match.title) out.title = String(match.title).slice(0, 180);
    if (match.vendor) out.vendor = String(match.vendor).slice(0, 120);
    if (match.url) out.url = String(match.url).split('?')[0];
    if (match.price != null) out.price = match.price;
    if (match.compareAtPrice != null) out.compare_at_price = match.compareAtPrice;
    if (typeof match.availableForSale === 'boolean') out.available = match.availableForSale;
    if (match.match_score != null) out.match_score = match.match_score;
    if (match.concentration) out.concentration = String(match.concentration).slice(0, 80);
    if (match.bottle_size) out.bottle_size = String(match.bottle_size).slice(0, 40);
    if (match.gender) out.gender = String(match.gender).slice(0, 40);
    return out;
  }

  function _imageSearchIntelPayload(data, matches, activeIndex, action) {
    matches = matches || [];
    var payload = {
      result_count: matches.length,
      active_rank: (activeIndex || 0) + 1
    };
    if (action) payload.action = action;
    if (data && data.found_by) payload.found_by = data.found_by;
    if (data && data.identification) {
      if (typeof data.identification === 'string') payload.identification = data.identification.slice(0, 180);
      else {
        try { payload.identification = JSON.stringify(data.identification).slice(0, 500); } catch (_) {}
      }
    }
    payload.active_match = _intelMatchPayload(matches[activeIndex || 0], activeIndex || 0);
    payload.top_matches = matches.slice(0, 5).map(function(match, i) { return _intelMatchPayload(match, i); });
    return payload;
  }

  // ── Similar Fragrances ──────────────────────────────────────────────

  var SIMILAR_PERSONAL_THRESHOLD = 60;

  function _brainCapability() {
    if (window.PriveBrainIntel
      && /^[A-Za-z0-9_-]{20,160}$/.test(window.PriveBrainIntel._vid || '')
      && /^[a-f0-9]{64}$/i.test(window.PriveBrainIntel._vidToken || '')) {
      return {
        vid: window.PriveBrainIntel._vid,
        vid_token: window.PriveBrainIntel._vidToken,
        handoff_ticket: window.PriveBrainIntel._handoffTicket,
        handoff_expires_at: window.PriveBrainIntel._handoffExpiresAt,
        handoff_ref: window.PriveBrainIntel._handoffRef,
        order_ticket: window.PriveBrainIntel._orderTicket,
        order_expires_at: window.PriveBrainIntel._orderExpiresAt
      };
    }
    var stores = [];
    try { stores.push(localStorage); } catch (e) {}
    try { stores.push(sessionStorage); } catch (e) {}
    for (var i = 0; i < stores.length; i++) {
      try {
        var stored = JSON.parse(stores[i].getItem('prive_brain_capability_v2') || 'null');
        if (stored
          && /^[A-Za-z0-9_-]{20,160}$/.test(stored.vid || '')
          && /^[a-f0-9]{64}$/i.test(stored.vid_token || '')) return stored;
      } catch (e) {}
    }
    return null;
  }

  // The handoff ticket for this tap and its reference for the tap event, or
  // null when there is no fresh one. The collector spends a ticket per tap
  // and asks for the next (takeHandoffTicket); without it (an older cached
  // collector, or none) the page's ticket is read as it is.
  function _takeHandoff() {
    var intel = window.PriveBrainIntel;
    if (intel && typeof intel.takeHandoffTicket === 'function') {
      try { return intel.takeHandoffTicket() || null; } catch (e) { return null; }
    }
    var capability = _brainCapability();
    if (!capability
      || !/^[A-Za-z0-9_-]{43}$/.test(capability.handoff_ticket || '')
      || Number(capability.handoff_expires_at) <= Date.now() + 60000) return null;
    return {
      ticket: capability.handoff_ticket,
      ref: /^[A-Za-z0-9_-]{22}$/.test(capability.handoff_ref || '') ? capability.handoff_ref : null
    };
  }

  // The one link line in every WhatsApp message, which the WhatsApp assistant
  // reads (the last line, except the camera result's note after it, where
  // the bot's fallback search keeps to the title): the page (a product's,
  // with the size, or the page the button sits on), which button it was
  // (`desde`), and at tap time the handoff ticket that links the chat to
  // this visit.
  function _whatsAppLinkLine(t, ticket) {
    var params = [];
    if (t.variant) params.push(['variant', String(t.variant)]);
    if (t.desde) params.push(['desde', t.desde]);
    if (ticket) params.push(['prive_handoff', ticket]);
    var rawUrl = t.url || '/';
    try {
      var parsed = new URL(rawUrl, window.location.origin);
      params.forEach(function(pair) { parsed.searchParams.set(pair[0], pair[1]); });
      return parsed.toString();
    } catch (e) {
      var hashIdx = rawUrl.indexOf('#');
      var base = hashIdx === -1 ? rawUrl : rawUrl.slice(0, hashIdx);
      var fragment = hashIdx === -1 ? '' : rawUrl.slice(hashIdx);
      params.forEach(function(pair) {
        base += (base.indexOf('?') === -1 ? '?' : '&') + pair[0] + '=' + encodeURIComponent(pair[1]);
      });
      return base + fragment;
    }
  }

  // The chat's opening message. A product (`title`, `url`, `variant`, an
  // optional `note` such as the camera's) opens with the product; any other
  // button (`text`) keeps its own words. Both end with the link line. A
  // WhatsApp link carries text only, never a photo.
  function _whatsAppHref(t, handoff) {
    var line = _whatsAppLinkLine(t, handoff && handoff.ticket);
    var text = t.text != null
      ? t.text + '\n' + line
      : 'Hola, me interesa este producto:\n' + (t.title || '') + '\n' + line + (t.note ? '\n' + t.note : '');
    return (t.wa || 'https://wa.me/' + WA_NUMBER) + '?text=' + encodeURIComponent(text);
  }

  // The WhatsApp address a theme link already points at (its number), so a
  // rebuilt message keeps it.
  function _waBase(link) {
    try {
      var parsed = new URL(link.href);
      if (parsed.hostname === 'wa.me' && /^\/\d{6,15}$/.test(parsed.pathname)) return 'https://wa.me' + parsed.pathname;
    } catch (e) {}
    return null;
  }

  // The size on show where a product button sits: its own form's (the buy
  // row's, or the sticky bar's main form), else the page's ?variant= when the
  // button is for this page's product.
  function _selectedVariantId(node, productUrl) {
    var form = null;
    if (node && typeof node.closest === 'function') {
      form = node.closest('form');
      var owner = form ? null : node.closest('[form-id]');
      if (owner) form = document.getElementById(owner.getAttribute('form-id'));
    }
    if (form && form.elements && typeof form.elements.namedItem === 'function') {
      var field = form.elements.namedItem('id');
      var value = field && (field.value || (field[0] && field[0].value));
      if (/^\d+$/.test(value || '')) return value;
    }
    try {
      var here = new URL(window.location.href);
      if (productUrl && new URL(productUrl, here.origin).pathname !== here.pathname) return '';
      var variant = here.searchParams.get('variant');
      return /^\d+$/.test(variant || '') ? variant : '';
    } catch (e) { return ''; }
  }

  // The page a button with no product sits on, with the search words on the
  // search page.
  function _currentPageUrl() {
    try {
      var here = new URL(window.location.href);
      var page = new URL(here.pathname, here.origin);
      var q = here.searchParams.get('q');
      if (q) page.searchParams.set('q', q);
      return page.toString();
    } catch (e) {
      return '/';
    }
  }

  // How long a WhatsApp tap waits for the intelligence service to renew its
  // handoff ticket before the tab goes to WhatsApp without one. The ticket
  // only links the chat to the browsing session; the chat is the sale.
  var WA_RENEW_TIMEOUT_MS = 3000;

  // Resolves once `work` settles (either way) or `ms` has passed, whichever
  // comes first. Never rejects: callers act the same on success, failure and
  // silence, and only read the capability afterwards to see which it was.
  function _settleWithin(work, ms) {
    return new Promise(function(resolve) {
      var timer = setTimeout(resolve, ms);
      var done = function() { clearTimeout(timer); resolve(); };
      Promise.resolve().then(function() { return work(); }).then(done, done);
    });
  }

  // The tap on a WhatsApp link, shared by the product page's button, the
  // camera's and every other WhatsApp link on the site (see
  // _bindPageWhatsApp). `target()` gives the message at tap time (the
  // camera's changes with the size and the candidate). The tap takes the
  // handoff ticket that links the chat to this browsing session. When there
  // is no fresh one, the tap holds a tab open while the intelligence service
  // renews it, then sends that tab to WhatsApp either way: with the fresh
  // ticket when the renewal lands, without one when it fails, rejects or
  // outlasts WA_RENEW_TIMEOUT_MS (before 2026-09-14 a failed renewal closed
  // the tab and the tap did nothing). A second tap during the renewal does
  // nothing. `after(target, handoff)` runs once per tap that went through,
  // for tracking, once the ticket the chat carries is known.
  function _whatsAppClick(link, target, after) {
    var renewalInFlight = null;
    function track(t, handoff) {
      if (!after) return;
      try { after(t, handoff); } catch (e) {}
    }
    return function(event) {
      if (renewalInFlight) {
        if (event && event.preventDefault) event.preventDefault();
        return;
      }
      var t = target() || {};
      var intel = window.PriveBrainIntel;
      var handoff = _takeHandoff();
      if (!handoff && intel && typeof intel.ensureFreshPurposeTickets === 'function') {
        if (event && event.preventDefault) event.preventDefault();
        var popup = null;
        try {
          popup = window.open('about:blank', '_blank');
          if (popup) {
            popup.opener = null;
            // A blank tab for up to three seconds reads as a broken tap.
            // Say what it is waiting for. about:blank opened from here is
            // same-origin, so the page can be written; guarded all the same.
            try {
              popup.document.title = 'WhatsApp';
              popup.document.body.innerHTML = '<p style="margin:0;padding:24px;font:500 14px/1.5 Jost,Barlow,sans-serif;letter-spacing:.08em;text-transform:uppercase;color:#192e49">Abriendo WhatsApp…</p>';
            } catch (e) {}
          }
        } catch (e) {}
        renewalInFlight = _settleWithin(function() { return intel.ensureFreshPurposeTickets(); }, WA_RENEW_TIMEOUT_MS);
        // The open tab gets its destination before the tracking runs, so it
        // goes whatever the tracking does. The ticket is taken now: the
        // fresh one if the renewal landed, none if not.
        renewalInFlight.then(function() {
          renewalInFlight = null;
          if (popup && popup.closed) {
            // The shopper closed the waiting tab before the service answered:
            // a cancel, not a failure. The store's own tab stays where it is,
            // and no ticket is spent on it.
            link.href = _whatsAppHref(t, null);
            track(t, null);
            return;
          }
          var fresh = _takeHandoff();
          var href = _whatsAppHref(t, fresh);
          link.href = href;
          if (!fresh) {
            // The chat goes out without its ticket, so it will not link back
            // to this browsing session. Silent otherwise; say so.
            try { console.warn('[prive-brain] WhatsApp opened without a handoff ticket'); } catch (e) {}
          }
          var sent = false;
          if (popup) {
            try { popup.location.href = href; sent = true; } catch (e) {}
          }
          if (!sent) {
            // No tab to send (blocked, or the assignment threw): the page
            // itself goes, as the success path always has.
            try {
              if (window.location.assign) window.location.assign(href);
              else window.location.href = href;
            } catch (e) {}
          }
          track(t, fresh);
        });
        return;
      }
      link.href = _whatsAppHref(t, handoff);
      track(t, handoff);
    };
  }

  // Every other WhatsApp link on the site (search, gift help, the store page,
  // the contact form, the footer, links in page text) gets the same tap: its
  // own words, then the link line with the page it sits on, which button it
  // is (`data-whatsapp-desde` on the link or a parent; the footer is `pie`,
  // anything else `contacto`) and the handoff ticket. `data-whatsapp-page`
  // names the page when it is not this one (the search with its words). A
  // link with no words of its own is left as it is and only counted. The
  // product page's and the camera's links are bound where they are drawn.
  function _bindPageWhatsApp() {
    document.addEventListener('click', function(event) {
      var start = event && event.target;
      var link = start && typeof start.closest === 'function' ? start.closest('a[href^="https://wa.me/"]') : null;
      if (!link || link._priveWaBound) return;
      var holder = link.closest('[data-whatsapp-desde]');
      var desde = holder ? holder.getAttribute('data-whatsapp-desde')
        : (link.closest('footer, .shopify-section-group-footer-group') ? 'pie' : 'contacto');
      var words = '';
      try { words = new URL(link.href).searchParams.get('text') || ''; } catch (e) {}
      // A message this handler already gave a link line ends with it; the
      // next tap replaces that line rather than adding a second.
      words = words.replace(/\n\S*[?&]desde=[a-z]+\S*$/, '');
      if (!link._priveWaTap) {
        link._priveWaTap = _whatsAppClick(link, function() { return link._priveWaTarget; }, function(t, handoff) {
          _trackPageWhatsApp(t.desde, handoff);
        });
      }
      if (!words.trim()) {
        _trackPageWhatsApp(desde, null);
        return;
      }
      link._priveWaTarget = {
        text: words,
        url: (holder && holder.getAttribute('data-whatsapp-page')) || _currentPageUrl(),
        desde: desde,
        wa: _waBase(link)
      };
      link._priveWaTap(event);
    });
  }

  function _trackPageWhatsApp(desde, handoff) {
    var intel = window.PriveBrainIntel;
    if (!intel || typeof intel.trackWhatsAppTap !== 'function') return;
    try {
      intel.trackWhatsAppTap(null, null, null, null, { surface: desde, ref: handoff && handoff.ref });
    } catch (e) {}
  }

  function brainPostSearch(body) {
    var payload = Object.assign({}, body || {});
    var cacheKey = 'prive_brain_search:' + JSON.stringify(payload);
    try {
      var hit = sessionStorage.getItem(cacheKey);
      if (hit) return Promise.resolve(JSON.parse(hit));
    } catch (e) {}

    var ctrl = new AbortController();
    var to = setTimeout(function() { ctrl.abort(); }, 12000);
    return fetch(API + '/api/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: ctrl.signal,
    }).then(function(r) {
      clearTimeout(to);
      if (!r.ok) throw new Error('Brain API ' + r.status);
      return r.json();
    }).then(function(json) {
      try { sessionStorage.setItem(cacheKey, JSON.stringify(json)); } catch (e) {}
      return json;
    }).catch(function(err) {
      clearTimeout(to);
      throw err;
    });
  }

  function _brainSimilarState(hasResults, count, label) {
    try {
      document.dispatchEvent(new CustomEvent('prive:brain-similar', {
        detail: { hasResults: !!hasResults, count: count || 0, label: label || '' },
      }));
    } catch (e) {}
  }

  function _hideShopifyRecommendationFallback() {
    var nodes = [];
    try { nodes = document.querySelectorAll('product-recommendations[intent="related"]'); } catch (e) { nodes = []; }
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      node.hidden = true;
      node.setAttribute('hidden', 'hidden');
      var section = node.closest ? node.closest('.shopify-section') : null;
      if (section) {
        section.hidden = true;
        section.setAttribute('hidden', 'hidden');
      }
    }
  }

  // Returns true once the widget has rendered and been classified, so the caller
  // can stop watching. False means "not resolved yet" — either no widget in the
  // DOM or it has not painted its contents.
  function _hideEmptyJudgeMeReviews() {
    var resolved = false;
    var widgets = [];
    try { widgets = document.querySelectorAll('#judgeme_product_reviews, .jdgm-review-widget, .jdgm-rev-widg'); } catch (e) { widgets = []; }
    for (var i = 0; i < widgets.length; i++) {
      var widget = widgets[i];
      if (!widget) continue;
      var section = widget.closest ? widget.closest('.shopify-section') : null;
      var text = ((widget.textContent || '') + ' ' + (section && section !== widget ? section.textContent || '' : '')).replace(/\s+/g, ' ').trim();
      var countAttr = widget.getAttribute ? widget.getAttribute('data-number-of-reviews') : null;
      var emptyByText = /(?:no ha recibido ninguna reseña|hasn.?t received any reviews yet|no reviews yet)/i.test(text);
      var emptyByData = String(countAttr || '') === '0' || /number_of_reviews[\\"']*\s*:?\s*0\b/i.test(text);
      // Resolve only on evidence the widget itself owns. `text` includes the
      // enclosing section, which now carries a configured heading and subheading
      // — always past 40 characters — so using it here marked a Judge.me
      // placeholder as rendered before it had hydrated. The observer then never
      // installed, and a widget that later resolved to "no reviews" stayed
      // visible.
      var widgetText = (widget.textContent || '').replace(/\s+/g, ' ').trim();
      if (countAttr !== null || widgetText.length > 40) resolved = true;
      if (!emptyByText && !emptyByData) continue;
      var target = section || widget;
      target.hidden = true;
      if (target.setAttribute) target.setAttribute('hidden', 'hidden');
      if (target.style) target.style.display = 'none';
      resolved = true;
    }
    return resolved;
  }

  // This ran the full hider on every mutation of document.body for 12 seconds,
  // undebounced. Each pass serialises the reviews section's textContent —
  // measured at 19,429 characters and 0.39ms on a live product page — and the
  // window it covers is exactly when seven app embeds are initialising and the
  // main thread is most contended.
  //
  // Three changes: coalesce a burst of mutations into one pass per frame, scope
  // the observer to the reviews section once it exists instead of the whole
  // body, and stop as soon as the widget has rendered rather than always
  // running the clock out.
  function _installEmptyJudgeMeReviewHider() {
    if (_hideEmptyJudgeMeReviews()) return;

    var SELECTOR = '#judgeme_product_reviews, .jdgm-review-widget, .jdgm-rev-widg';
    var observer = null, timer = null, safety = null, queued = false;

    function stop() {
      if (observer) { observer.disconnect(); observer = null; }
      if (timer) { clearTimeout(timer); timer = null; }
      if (safety) { clearTimeout(safety); safety = null; }
    }

    function run() {
      queued = false;
      if (_hideEmptyJudgeMeReviews()) stop();
    }

    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(run);
    }

    try {
      observer = new MutationObserver(schedule);
      var widget = document.querySelector(SELECTOR);
      var scope = (widget && widget.closest && widget.closest('.shopify-section')) || document.body;
      observer.observe(scope, { childList: true, subtree: true });
    } catch (e) {
      return;
    }

    // One delayed sweep in case the widget arrives by a route that does not
    // mutate the observed scope, then a hard stop.
    safety = setTimeout(run, 2000);
    timer = setTimeout(stop, 12000);
  }

  // The host sits inside a section that contributes its own vertical spacing, so
  // hiding the component alone still leaves a gap where the row would have been.
  function _similarSectionOf(el) {
    try {
      return (el.closest && el.closest('.shopify-section')) || null;
    } catch (e) {
      return null;
    }
  }

  function _collapseSimilarSection(el) {
    var section = _similarSectionOf(el);
    if (section) section.classList.add('brain-similar-collapsed');
  }

  // The component reloads once the visitor capability resolves, and that
  // personalized attempt can find products where the anonymous one found none.
  // Without this the section stayed display:none for the rest of the page life
  // and the recovered results were rendered into a hidden wrapper.
  function _expandSimilarSection(el) {
    var section = _similarSectionOf(el);
    if (section) section.classList.remove('brain-similar-collapsed');
  }

  var BrainSimilar = class extends HTMLElement {
    connectedCallback() {
      if (this._loaded) return;
      this._loaded = true;
      this._setSeed(document.querySelector('[data-brain-similar-seed]'));
      var handle = this.getAttribute('product-handle');
      if (!handle) return;

      // Reserve the row before the fetch starts, so results do not shift the
      // page in when they land.
      var skeletonCards = '';
      for (var sc = 0; sc < 6; sc++) skeletonCards += '<div class="brain-similar__skeleton-card"></div>';
      var skeleton =
        '<div class="brain-similar__skeleton" aria-hidden="true">' +
          '<div class="brain-similar__skeleton-header"></div>' +
          '<div class="brain-similar__skeleton-row">' + skeletonCards + '</div>' +
        '</div>';

      var self = this;
      var limit = parseInt(this.getAttribute('limit'), 10) || 6;
      var lastCapabilityKey = null;
      function load() {
        var capability = _brainCapability();
        lastCapabilityKey = capability ? capability.vid + ':' + capability.vid_token : '';
        var currentGeneration = self._similarGeneration = (self._similarGeneration || 0) + 1;
        _expandSimilarSection(self);
        self.innerHTML = skeleton;
        self.setAttribute('data-state', 'loading');
        self._load(limit)
          .then(function(products) {
            if (currentGeneration !== self._similarGeneration) return;
            if (products.length > 0) {
              self._render(products.slice(0, limit));
              self.setAttribute('data-state', 'ready');
              _hideShopifyRecommendationFallback();
              _brainSimilarState(true, products.length, self._labelFor(products));
              if (window.PriveBrainIntel && typeof window.PriveBrainIntel._track === 'function') {
                try {
                  window.PriveBrainIntel._track('similar_impression', {
                    handle: self.getAttribute('product-handle'), result_count: products.length, mode: self._labelFor(products),
                    top_results: products.slice(0, 5).map(function(p, i) { return _intelMatchPayload(p, i); }),
                  });
                } catch (e) {}
              }
            } else {
              self.setAttribute('data-state', 'empty');
              _collapseSimilarSection(self);
              _brainSimilarState(false, 0, '');
            }
          })
          .catch(function(err) {
            if (currentGeneration !== self._similarGeneration) return;
            self.setAttribute('data-state', 'error');
            _collapseSimilarSection(self);
            _brainSimilarState(false, 0, '');
            console.warn('[Brain] similar-fragrances error:', err);
          });
      }
      load();
      this._onBrainCapabilityReady = function() {
        var capability = _brainCapability();
        var key = capability ? capability.vid + ':' + capability.vid_token : '';
        if (key !== lastCapabilityKey) load();
      };
      window.addEventListener('prive:brain-vid-ready', this._onBrainCapabilityReady);
      // The similarity row lives outside product-rerender. Capture the event
      // before that host replaces its form; the event does not bubble.
      this._onSimilarProductRerender = function(event) {
        var formId = event.target && event.target.getAttribute && event.target.getAttribute('id');
        if (self._similarFormId ? formId !== self._similarFormId : !/^product-form-main-/.test(formId || '')) return;
        var fragment = event.detail && event.detail.htmlFragment;
        var seed = fragment && fragment.querySelector('[data-brain-similar-seed]');
        if (seed) {
          if (self._setSeed(seed)) load();
        } else if (event.detail && event.detail.productChange) {
          // Never carry the old scent into an unidentified sibling product.
          self.setAttribute('variant-id', '');
          load();
        }
      };
      this._onSimilarVariantChange = function(event) {
        var formId = event.target && event.target.getAttribute && event.target.getAttribute('id');
        if (self._similarFormId ? formId !== self._similarFormId : !/^product-form-main-/.test(formId || '')) return;
        var id = event.detail && event.detail.variant && event.detail.variant.id;
        var variantId = /^\d+$/.test(String(id || '')) ? 'gid://shopify/ProductVariant/' + id : '';
        if (variantId !== self.getAttribute('variant-id')) {
          self.setAttribute('variant-id', variantId);
          load();
        }
      };
      document.addEventListener('product:rerender', this._onSimilarProductRerender, true);
      document.addEventListener('variant:change', this._onSimilarVariantChange, true);
    }

    disconnectedCallback() {
      this._loaded = false;
      this._similarGeneration = (this._similarGeneration || 0) + 1;
      if (this._onBrainCapabilityReady) {
        window.removeEventListener('prive:brain-vid-ready', this._onBrainCapabilityReady);
        this._onBrainCapabilityReady = null;
      }
      document.removeEventListener('product:rerender', this._onSimilarProductRerender, true);
      document.removeEventListener('variant:change', this._onSimilarVariantChange, true);
    }

    _setSeed(seed) {
      if (!seed) return false;
      this._similarFormId = seed.getAttribute('form-id') || this._similarFormId;
      var self = this;
      var changed = false;
      ['product-handle', 'product-gid', 'variant-id'].forEach(function(name) {
        var value = seed.getAttribute(name) || '';
        if (self.getAttribute(name) !== value) { self.setAttribute(name, value); changed = true; }
      });
      return changed;
    }

    _load(limit) {
      var variantId = this.getAttribute('variant-id') || '';
      var productGid = this.getAttribute('product-gid') || '';
      var sourceHandle = this.getAttribute('product-handle') || '';
      // Similarity starts from a selected fragrance. Never fall back to title
      // or vendor keyword searches and present their results as scent matches.
      if (!variantId) return Promise.resolve([]);
      var body = { variantId: variantId, limit: Math.max(limit * 3, 12) };
      var capability = _brainCapability();
      if (capability) {
        body.vid = capability.vid;
        body.vid_token = capability.vid_token;
      }
      return brainPostSearch(body).then(function(data) {
        var seen = {};
        var products = ((data && data.results) || []).filter(function(p) {
          if (!p || !p.handle || !p.title || !p.url || p.availableForSale !== true) return false;
          if ((sourceHandle && p.handle === sourceHandle) || (productGid && p.id === productGid)) return false;
          if (p.method !== 'scent-profile' || !p.evidence || !Array.isArray(p.evidence.sharedNotes) || !p.evidence.sharedNotes.length || !p.reason) return false;
          var key = String(p.handle || p.id);
          if (seen[key]) return false;
          seen[key] = true;
          return true;
        }).slice(0, limit);
        return products;
      });
    }

    _labelFor() {
      return 'Fragancias similares';
    }

    _render(products) {
      this.textContent = '';
      var label = this._labelFor(products);
      var section = el('div', 'brain-similar');
      var header = el('div', 'brain-similar__header');
      header.appendChild(el('p', 'brain-similar__eyebrow heading heading--small', 'Según este aroma'));
      header.appendChild(el('h2', 'brain-similar__title heading h3', label));
      section.appendChild(header);
      var scroll = el('div', 'brain-similar__scroll');
      var list = el('div', 'brain-similar__list');
      var sourceHandle = this.getAttribute('product-handle');
      products.forEach(function(p) {
        var card = el('a', 'brain-similar__card');
        card.href = p.url;
        card.addEventListener('click', function() {
          if (window.PriveBrainIntel) window.PriveBrainIntel.trackSimilarClick(p.handle, sourceHandle);
        });
        if (p.image) {
          var img = document.createElement('img');
          img.src = _sizedImage(p.image, 360); img.alt = p.title || '';
          img.loading = 'lazy'; img.width = 180; img.height = 180;
          img.className = 'brain-similar__image';
          card.appendChild(img);
        }
        var info = el('div', 'brain-similar__info');
        info.appendChild(el('span', 'brain-similar__match text--xsmall', 'Notas en común'));
        if (p.vendor) info.appendChild(el('span', 'brain-similar__vendor text--xsmall text--subdued', p.vendor));
        info.appendChild(el('p', 'brain-similar__name text--small', _titleWithoutVendor(p.title, p.vendor)));
        if (p.reason) info.appendChild(el('p', 'brain-similar__reason text--xsmall text--subdued', p.reason));
        var pw = el('div', 'brain-similar__price');
        if (p.compareAtPrice && p.compareAtPrice > p.price) {
          pw.appendChild(el('span', 'price price--highlight', fmtPrice(p.price)));
          pw.appendChild(el('span', 'price price--compare', fmtPrice(p.compareAtPrice)));
        } else {
          pw.appendChild(el('span', 'price', fmtPrice(p.price)));
        }
        info.appendChild(pw);
        card.appendChild(info);
        list.appendChild(card);
      });
      scroll.appendChild(list);
      section.appendChild(scroll);
      this.appendChild(section);
    }
  };
  customElements.define('brain-similar-fragrances', BrainSimilar);

  // ── Fragrance Profile ───────────────────────────────────────────────

  var BrainProfile = class extends HTMLElement {
    connectedCallback() {
      var self = this;
      var handle = this.getAttribute('product-handle');
      if (!handle) return;
      brainFetch('/api/products/' + handle + '/profile')
        .then(function(data) { if (data.profile) self._render(data.profile); })
        .catch(function(err) { console.warn('[Brain] fragrance-profile error:', err); });
    }
    _render(profile) {
      var hasNotes = profile.notas_salida || profile.notas_corazon || profile.notas_fondo;
      var hasMeta = profile.familia || profile.concentracion;
      if (!hasNotes && !hasMeta) return;
      this.textContent = '';
      var w = el('div', 'brain-profile');
      if (hasMeta) {
        var meta = el('div', 'brain-profile__meta');
        if (profile.familia) meta.appendChild(el('span', 'brain-profile__badge', profile.familia));
        if (profile.concentracion) meta.appendChild(el('span', 'brain-profile__badge brain-profile__badge--accent', profile.concentracion));
        if (profile.genero) {
          var gl = profile.genero === 'M' ? 'Masculino' : profile.genero === 'F' ? 'Femenino' : 'Unisex';
          meta.appendChild(el('span', 'brain-profile__badge brain-profile__badge--subtle', gl));
        }
        w.appendChild(meta);
      }
      if (hasNotes) {
        var pyr = el('div', 'brain-profile__pyramid');
        pyr.appendChild(el('p', 'brain-profile__pyramid-title heading heading--xsmall', 'Pirámide Olfativa'));
        if (profile.notas_salida) pyr.appendChild(this._noteRow('Salida', profile.notas_salida, 'top'));
        if (profile.notas_corazon) pyr.appendChild(this._noteRow('Corazón', profile.notas_corazon, 'heart'));
        if (profile.notas_fondo) pyr.appendChild(this._noteRow('Fondo', profile.notas_fondo, 'base'));
        w.appendChild(pyr);
      }
      this.appendChild(w);
    }
    _noteRow(label, notes, tier) {
      var row = el('div', 'brain-profile__note-row brain-profile__note-row--' + tier);
      row.appendChild(el('span', 'brain-profile__note-label text--xsmall text--subdued', label));
      var str = Array.isArray(notes) ? notes.join(', ') : String(notes);
      row.appendChild(el('span', 'brain-profile__note-values text--small', str));
      return row;
    }
  };
  customElements.define('brain-fragrance-profile', BrainProfile);

  // ── Image Search — the camera ───────────────────────────────────────
  //
  // One drawer, `<brain-image-search>`, opened by the header camera icon and
  // by any `[data-brain-image-search-open]` control. It is a body-level
  // singleton over the current page, no navigation, and it works on two
  // planes (DESIGN.md, Camera Search):
  //
  //   The live plane: the camera fills the screen on Navy Deep. Its chrome
  //   is smoked glass (Navy Deep at 66% with a blur and a lit top edge), the
  //   only translucent surface in the system, because here the picture is
  //   the surface. Four white brackets aim; they tighten on capture and turn
  //   Amber Ember only when a product is found.
  //
  //   The paper plane: the result is Privé paper floating over the live
  //   picture. It springs to a 34% peek, drags to its highest detent 12px
  //   under the glass row, so the close control and "Otra foto" are never
  //   covered, and reads in the product page's own order: identity, price,
  //   sizes, the buy bar, then the trust strip, the dossier and the other
  //   candidates. The state sheets (no match, search down, camera blocked,
  //   no camera) are the same paper, as tall as what they say.
  //
  // The search is one request: POST /api/search with the photo, answered
  // server-side hydrated (price, sizes, availability, the fragrance_data
  // profile). The photo is a lead, never proof a bottle is original; the
  // copy says so. If Brain is down the sheet offers the store's own text
  // search and WhatsApp, which never depend on it.

  var _drawer = null;
  var _maxMatches = 8;

  // ── Helpers shared with the result rendering ──
  function _translateGenero(g) {
    if (!g) return '';
    var k = String(g).toUpperCase();
    if (k === 'M' || k === 'MAN' || k === 'MEN') return 'Masculino';
    if (k === 'F' || k === 'W' || k === 'WOMAN' || k === 'WOMEN') return 'Femenino';
    if (k === 'U' || k === 'UNISEX') return 'Unisex';
    return g;
  }

  // The title is the catalogue's own word for the variant family ("… EDP
  // (M) / 105 ml"); the metaobject only fills the gap. The 200 ml Club de
  // Nuit Iconic metaobject says Extrait while its title says EDP, and the
  // title is what the shop sells.
  function _concentrationFromTitle(title) {
    if (!title) return '';
    var t = String(title), intense = /\bIntense\b/i.test(t);
    // The shop's titles carry the token (EDP, EDT, EDC); a title that spells
    // the phrase out instead says the same, and the token wins where both
    // appear ("L'Homme Cologne Bleue EDT" is an EDT).
    if (/\bEDP\b/i.test(t)) return intense ? 'EDP Intense' : 'Eau de Parfum';
    if (/\bEDT\b/i.test(t)) return 'Eau de Toilette';
    if (/\bEDC\b/i.test(t)) return 'Eau de Cologne';
    if (/\bExtrait\b/i.test(t)) return 'Extrait de Parfum';
    if (/\bEau de Parfum\b/i.test(t)) return intense ? 'EDP Intense' : 'Eau de Parfum';
    if (/\bEau de Toilette\b/i.test(t)) return 'Eau de Toilette';
    if (/\bCologne\b/i.test(t)) return 'Eau de Cologne';
    if (/\bParfum\b/i.test(t)) return 'Parfum';
    return '';
  }
  function _bottleSizeFromTitle(title) {
    if (!title) return '';
    var m = String(title).match(/(\d+(?:[.,]\d+)?)\s*ml\b/i);
    return m ? (m[1] + ' ml') : '';
  }
  function _generoFromTitle(title) {
    var t = String(title || '');
    if (/\(M\)/.test(t)) return 'Masculino';
    if (/\((W|F)\)/.test(t)) return 'Femenino';
    if (/\(U\)/.test(t)) return 'Unisex';
    return '';
  }
  // Brain returns the product's full-size CDN file, which on a phone means
  // downloading a 1–2 MB original to fill a 96px thumbnail. Shopify's CDN
  // resizes on request, so ask for twice the box (retina) instead.
  // Anything that is not a Shopify CDN URL is passed through untouched.
  function _sizedImage(url, width) {
    var u = String(url || '');
    if (!u || !/^(https?:)?\/\/(cdn\.shopify\.com|[^/]+\/cdn\/shop)\//.test(u)) return u;
    if (/[?&]width=/.test(u)) return u;
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }
  // A URL as a CSS background-image value, its quotes and backslashes escaped.
  function _cssUrl(url) {
    return 'url("' + String(url || '').replace(/[\\"\n\r]/g, function(c) { return '\\' + c.charCodeAt(0).toString(16) + ' '; }) + '")';
  }
  function _titleWithoutVendor(title, vendor) {
    var t = String(title || '');
    if (!vendor) return t;
    var prefix = String(vendor) + ' ';
    if (t.slice(0, prefix.length).toLowerCase() === prefix.toLowerCase()) return t.slice(prefix.length);
    return t;
  }
  // Strip the vendor prefix and the trailing "EDT (M) / 105 ml" tail so the
  // title shows the clean fragrance name.
  function _cleanTitle(title, vendor) {
    if (!title) return '';
    var t = String(title);
    if (vendor) {
      var v = String(vendor);
      if (t.toLowerCase().indexOf(v.toLowerCase()) === 0) t = t.substring(v.length).replace(/^[\s\-·]+/, '');
    }
    t = t.replace(/\s*\/\s*\d+(?:[.,]\d+)?\s*(?:ml|pcs?)\b.*$/i, '');
    t = t.replace(/\s*\((?:[MFUW]|Unisex|Men|Women)\)\s*/i, ' ');
    t = t.replace(/\s+(EDP|EDT|EDC|Parfum|Extrait de Parfum|Extrait|Eau de Parfum|Eau de Toilette|Eau de Cologne|Cologne)(\s+Intense)?\s*$/i, '');
    return t.replace(/\s{2,}/g, ' ').trim();
  }
  // Free-shipping threshold (HNL) is read from a meta tag the theme emits.
  // Absent, the label never shows.
  // The store's free-shipping threshold (settings.cart_free_shipping_threshold,
  // resolved for the cart's currency by snippets/js-variables.liquid), in
  // the shop currency; null when the store does not advertise one.
  function _freeShippingThreshold() {
    var tv = window.themeVariables || {};
    var n = parseFloat((tv.settings || {}).freeShippingThreshold);
    return isNaN(n) || n <= 0 ? null : n;
  }
  function _isAvailable(match) {
    if (!match) return false;
    if (typeof match.availableForSale === 'boolean') return match.availableForSale;
    return !(match.inventory != null && match.inventory <= 0);
  }
  function _numericId(gid) {
    var m = String(gid || '').match(/(\d+)$/);
    return m ? m[1] : '';
  }
  function _sizeMl(match) {
    var s = (match && (match.bottle_size || _bottleSizeFromTitle(match.title))) || '';
    var n = parseFloat(String(s).replace(',', '.'));
    return isNaN(n) ? null : n;
  }
  // The verdicts, as the product page states them (fragrance-data.liquid):
  // the 60% threshold is applied to raw vote totals, never to rounded
  // percents, so 119/200 (59.5%) stays Todo el año.
  function _climaVerdict(c) {
    if (!c) return null;
    var fresh = Number(c.fresh) || 0, warm = Number(c.warm) || 0;
    var total = fresh + warm;
    if (total <= 0) return null;
    if (fresh * 100 >= 60 * total) return 'Clima caluroso';
    if (warm * 100 >= 60 * total) return 'Clima fresco';
    return 'Todo el año';
  }
  function _ocasionVerdict(o) {
    if (!o) return null;
    var dia = Number(o.dia) || 0, noche = Number(o.noche) || 0;
    var total = dia + noche;
    if (total <= 0) return null;
    if (dia * 100 >= 60 * total) return 'Día';
    if (noche * 100 >= 60 * total) return 'Noche';
    return 'Día y noche';
  }
  function _votesText(n) {
    n = Number(n) || 0;
    if (n <= 0) return '';
    if (n === 1) return '1 valoración';
    if (n >= 10000) return Math.floor(n / 1000) + ' mil valoraciones';
    return n.toLocaleString('es-HN') + ' valoraciones';
  }

  // Results that differ only by bottle size are one perfume in several
  // sizes, not several matches. Concentration stays in the key so an EDT
  // and an EDP of the same name are offered as different perfumes.
  function _familyKey(match) {
    match = match || {};
    var conc = _concentrationFromTitle(match.title) || match.concentration || (match.profile && match.profile.concentracion) || '';
    var name = _cleanTitle(match.title, match.vendor) || match.title || match.handle || '';
    return [match.vendor || '', name, conc].join('|').toLowerCase().replace(/\s+/g, ' ').trim();
  }
  function _groupMatches(matches) {
    var groups = [];
    var byKey = {};
    (matches || []).forEach(function(m, i) {
      var k = _familyKey(m);
      if (!byKey[k]) { byKey[k] = { key: k, items: [] }; groups.push(byKey[k]); }
      byKey[k].items.push(i);
    });
    groups.forEach(function(g) {
      g.items.sort(function(a, b) { return (_sizeMl(matches[a]) || 0) - (_sizeMl(matches[b]) || 0); });
    });
    return groups;
  }

  function _identLabel(ident) {
    if (!ident || typeof ident !== 'object') return '';
    var brand = String(ident.brand || '').trim();
    var name = String(ident.name || '').trim();
    if (brand && name.toLowerCase().indexOf(brand.toLowerCase()) === 0) return name;
    return (brand + ' ' + name).trim();
  }
  // The label as the shop's search gets it: the label's words, then the
  // concentration in the shop's own token ("EDT", "Intense EDP") when the
  // service named one, in its field or at the end of the name. The shop
  // gives ten products at most, so a family with more than ten (212, its
  // flankers and their sizes) needs the token to bring the right
  // concentration inside the ten; on the shop's search a token reorders
  // and never excludes, so a product the shop titles otherwise still comes.
  var _CONC_TOKENS = { extrait: 'Extrait', parfum: 'Parfum', edp: 'EDP', edt: 'EDT', edc: 'EDC' };
  function _labelQuery(ident) {
    var q = _identLabel(ident);
    if (!q) return q;
    var name = _labelName(ident.name, _labelWords(ident.brand));
    var field = _concKey(ident.concentration);
    // A full phrase that ends the name yields to a field that says otherwise
    // (_labelFit), so it leaves the query too: "Sauvage Eau de Parfum" with
    // the field "Eau de Toilette" goes as "Dior Sauvage EDT". The one word
    // "Parfum" is the name's own then, and stays.
    if (field && name.conc && field !== name.conc && !name.bare) {
      var s = String(ident.name || '').trim(), m = s.match(_TRAILING_CONC);
      if (m) q = _identLabel({ brand: ident.brand, name: s.slice(0, m.index) });
    }
    var token = _CONC_TOKENS[field || name.conc] || '';
    var has = token ? new RegExp('\\b' + token + '\\b', 'i') : null;
    var intense = (name.intense || _isIntense(ident.concentration)) && !/\bintense\b/i.test(q);
    // Intense goes where the shop's titles keep it, before the token, also
    // when the label carries the token already ("Libre EDP" with the field
    // "Eau de Parfum Intense" goes as "Libre Intense EDP"); nothing twice.
    if (has && has.test(q)) return intense ? q.replace(has, 'Intense $&') : q;
    if (!token) return intense ? q + ' Intense' : q;
    return q + (intense ? ' Intense ' : ' ') + token;
  }

  // ── Found by the label ──
  // The photo service reads a label well ("Alexandre.J The Collector Black
  // Muscs") but matches the catalogue by the shop's own spelling ("Alexandre
  // J. Black Muscs"), and comes back empty. The shop's search forgives
  // spelling, so the label goes there next; what it names is then hydrated
  // by the service's text search, in the shape of a photo match.
  // One request's signal under the drawer's cancel, with a budget of its
  // own: the parent's abort ends it, and so does its timer. stop() releases
  // both once the request is answered. Each request keeps its own, so one
  // that runs out of time does not end the one that follows it.
  function _timedSignal(parent, ms) {
    var ctl = new AbortController();
    var onAbort = function() { ctl.abort(); };
    if (parent.signal.aborted) ctl.abort();
    else parent.signal.addEventListener('abort', onAbort);
    var tm = setTimeout(function() { ctl.abort(); }, ms);
    ctl.stop = function() { clearTimeout(tm); parent.signal.removeEventListener('abort', onAbort); };
    return ctl;
  }

  function _labelWorthSearching(ident) {
    if (!ident || typeof ident !== 'object' || !String(ident.name || '').trim()) return false;
    var c = ident.confidence;
    if (!(c == null || isNaN(Number(c)) || Number(c) >= 0.7)) return false;
    // A one-word name without a brand answers nothing (_labelFit); the
    // Intense read off a name counts as one of its words there.
    var name = _labelName(ident.name, _labelWords(ident.brand));
    var words = name.words.filter(function(w) { return !_SET_WORDS.test(w); }).length;
    return String(ident.brand || '').trim() ? words > 0 : words + (name.intense ? 1 : 0) >= 2;
  }
  function _alnum(s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '');
  }
  // "Alexandre.J" is "Alexandre J."; "Dior" is "Christian Dior".
  function _vendorFits(brand, vendor) {
    var b = _alnum(brand), v = _alnum(vendor);
    if (!b || !v) return false;
    return b === v || (b.length >= 4 && v.indexOf(b) !== -1) || (v.length >= 4 && b.indexOf(v) !== -1);
  }
  // Words that never name a perfume: articles, units and the shop's tokens.
  var _NAME_NOISE = /^(de|du|des|la|le|les|el|the|ml|oz|fl|edp|edt|edc)$/;
  // Words that say for whom. Next to a name they say nothing ("The One for
  // Men" is The One, "Aventus For Her" is Aventus); they are the name when
  // nothing else is ("Pour Homme" is Versace's perfume, "Man" is Bvlgari's,
  // "Her" is Burberry's), or when only the brand's own word stands with
  // them ("Dior Homme").
  var _RECIPIENT_WORDS = /^(for|pour|man|men|him|woman|women|her|homme|femme|hombre|mujer|unisex)$/;
  // Whom the words say the perfume is for: "for men", "pour homme" and
  // "for him" are one key, and so are the shop's (M), (W) and (U) marks on
  // a title.
  var _MEN_WORDS = /^(man|men|him|homme|hombre)$/, _WOMEN_WORDS = /^(woman|women|her|femme|mujer)$/;
  function _whoOf(words) {
    for (var i = 0; i < words.length; i++) {
      if (_MEN_WORDS.test(words[i])) return 'm';
      if (_WOMEN_WORDS.test(words[i])) return 'w';
      if (words[i] === 'unisex') return 'u';
    }
    return '';
  }
  function _whoFromTitle(title) {
    var t = String(title || '');
    if (/\((?:M|Men|Hombres?)\)/i.test(t)) return 'm';
    if (/\((?:W|F|Women|Mujer(?:es)?)\)/i.test(t)) return 'w';
    if (/\((?:U|Unisex)\)/i.test(t)) return 'u';
    return '';
  }
  function _whoKey(s) {
    s = String(s || '').trim().toLowerCase();
    if (/^(m|man|men|male|masculino|hombre|homme)$/.test(s)) return 'm';
    if (/^(w|f|woman|women|female|femenino|mujer|femme)$/.test(s)) return 'w';
    if (/^(u|unisex)$/.test(s)) return 'u';
    return '';
  }
  // A concentration at the end of a name is read off it, and says which
  // one the label means: "Sauvage Eau de Parfum" is Sauvage, the EDP. Kept
  // elsewhere, the words are the name's own ("Le Parfum", "Eau Sauvage").
  // A phrase that may be a name's own ("I Want Choo Le Parfum") is read
  // off all the same; whose it is, the name's or the concentration's,
  // _labelFit decides by the label's field and _productName by the title's
  // token.
  // "Intense" qualifies the concentration, not the name, wherever it is
  // read: "Libre Eau de Parfum Intense" and "Libre Intense" both name
  // Libre, the intense one, and the shop titles it "Libre Intense EDP".
  var _TRAILING_CONC = /\s+(extrait de parfum|extrait|eau de parfum|eau de toilette|eau de cologne|edp|edt|edc|cologne|parfum|perfume)(\s+intense)?\s*$/i;
  function _labelName(text, known) {
    var s = String(text || '').trim(), name = s, conc = '', intense = false, bare = false;
    var m = s.match(_TRAILING_CONC);
    if (m && _labelWords(s.slice(0, m.index), known).length) { name = s.slice(0, m.index); conc = _concKey(m[1]); intense = !!m[2]; bare = /^(parfum|cologne|perfume)$/i.test(m[1]); }
    name = name.replace(/\bintense\b/ig, function() { intense = true; return ' '; });
    // bare: the one word "Parfum", "Cologne" or "Perfume" ("Le Parfum", "Dior
    // Homme Cologne", "The Icon The Perfume"), which a name may own; a full
    // phrase ("Eau de Parfum") or a token ("EDT") never is.
    return { blank: !s, words: _labelWords(name, known), conc: conc, bare: bare, intense: intense, who: _whoOf(_rawWords(name)) };
  }
  function _isIntense(s) {
    return /\bintense\b/i.test(String(s || ''));
  }
  // The shop's side of the same reading: the title without its vendor,
  // size, recipient and trailing token, read as a label is. The token is
  // the concentration; a phrase the title keeps before it is the name's
  // own when it says another one ("Le Beau Le Parfum EDP", "I Want Choo Le
  // Parfum EDP") and the token's own word when it says the same ("Her Eau
  // de Parfum Intense EDP"); without a token, the phrase is the
  // concentration. Intense is read off the whole title, since the token's
  // own ("… EDP Intense") goes with the token.
  function _productName(product) {
    var name = _labelName(_cleanTitle(product.title, product.vendor), _labelWords(product.vendor));
    var token = _concKey(_concentrationFromTitle(product.title));
    name.phrase = (name.bare && token && name.conc !== token) ? name.conc : '';
    if (token) name.conc = token;
    name.intense = _isIntense(product.title);
    name.who = name.who || _whoFromTitle(product.title);
    return name;
  }
  // Words that name a line, not a juice: they may lead a label and still
  // name the same perfume ("The Collector Black Muscs" is "Black Muscs").
  var _COLLECTION_WORDS = /^(collector|collectors|collection|collections|collezione|coleccion|linea|line|serie|series)$/;
  var _SET_WORDS = /^(value|set|estuche|gift|coffret|kit|duo|trio|pack|pc|pcs|sp|mini|miniatura)$/;
  // Sizes are never a name ("100 ml", "3.4 fl. oz"); numbers and single
  // letters otherwise are ("212", "9PM", "Y").
  var _SIZE_WORDS = /\b\d+(?:[.,]\d+)?\s*(?:ml|fl\.?\s*oz|oz|fl)\b\.?/g;
  function _rawWords(text) {
    return String(text || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(_SIZE_WORDS, ' ')
      .split(/[^a-z0-9]+/)
      .filter(function(w) { return w && !_NAME_NOISE.test(w); });
  }
  // The words of a name: the recipient words drop out while something else
  // names it, other than the brand's own words (known), and stay otherwise.
  function _labelWords(text, known) {
    var raw = _rawWords(text);
    var rest = raw.filter(function(w) { return !_RECIPIENT_WORDS.test(w); });
    var own = rest.filter(function(w) { return !known || known.indexOf(w) === -1; });
    return own.length ? rest : raw;
  }
  function _concKey(s) {
    s = String(s || '').toLowerCase();
    if (!s) return '';
    if (/extrait/.test(s)) return 'extrait';
    if (/edp|eau de parfum/.test(s)) return 'edp';
    if (/edt|toilette/.test(s)) return 'edt';
    if (/edc|cologne/.test(s)) return 'edc';
    if (/parfum|perfume/.test(s)) return 'parfum';
    return '';
  }
  function _isSet(product, words) {
    for (var i = 0; i < words.length; i++) if (_SET_WORDS.test(words[i])) return true;
    return /estuche|set/i.test(product.type || product.productType || '');
  }
  // How well a shop product answers the label: 2 when it is the perfume the
  // label names, 1 when it is the closest thing we carry (another
  // concentration, the set of the perfume, or the base perfume of a label
  // that leads with a flanker's word), 0 when it does not answer at all. The
  // product's own name must close the identified name: "Black Muscs" closes
  // "The Collector Black Muscs" (a collection name leads, same perfume),
  // "Good Girl" closes "Very Good Girl" (a flanker's word leads: closest,
  // not the perfume), "Sauvage" does not close "Sauvage Elixir" (a flanker
  // follows), nor does "Sauvage Elixir" answer "Sauvage". With a brand on
  // the label the vendor must fit it; without one, a single word is not a
  // name. The label's concentration is its own field, or the phrase that
  // ends its name, and "Intense" on either side is the concentration's
  // word: the regular Eau de Parfum is the closest to the intense one,
  // never the perfume. Whom the label names ("for men", "pour homme") must
  // be whom the title names, by its word or its (M)/(W)/(U) mark: The One
  // for Men is not The One. A label with a brand and no name at all is
  // answered by any product of that brand; a name that reads as nothing
  // ("Gift Set") answers none. Only a 2 opens the sheet without "Tu foto
  // parece ser…".
  function _labelFit(ident, product) {
    if (!ident || typeof ident !== 'object' || !product || !product.handle || !product.title) return 0;
    var brand = String(ident.brand || '').trim();
    if (brand && !_vendorFits(brand, product.vendor)) return 0;
    var known = _labelWords(brand + ' ' + (product.vendor || ''));
    var name = _labelName(ident.name, known);
    var all = name.words;
    var want = all.filter(function(w) { return !_SET_WORDS.test(w); });
    var labelIsSet = want.length !== all.length;
    if (!want.length) return (brand && name.blank) ? 2 : 0;
    if (!brand && want.length + (name.intense ? 1 : 0) < 2) return 0;
    var shop = _productName(product);
    // Unisex, on either side, contradicts no one.
    var who = name.who || _whoKey(ident.gender);
    if (who && shop.who && who !== shop.who && who !== 'u' && shop.who !== 'u') return 0;
    var core = shop.words.filter(function(w) { return !_SET_WORDS.test(w); });
    // The title may lead with the brand's own word where the label did not
    // ("Dior Homme" answers a label that reads "Homme").
    while (core.length > want.length && known.indexOf(core[0]) !== -1) core = core.slice(1);
    if (!core.length || core.length > want.length) return 0;
    var lead = want.length - core.length;
    for (var i = 0; i < core.length; i++) if (want[lead + i] !== core[i]) return 0;
    // What the label says before the product's name: the brand or a line
    // keeps the perfume, any other word makes this the base of a flanker.
    for (var j = 0; j < lead; j++) {
      if (!_COLLECTION_WORDS.test(want[j]) && known.indexOf(want[j]) === -1) return 1;
    }
    // The concentration: the label's field, else the phrase that ends its
    // name. The one word "Parfum" ("Le Parfum") may be a name's own: it is
    // when the field says another concentration ("I Want Choo Le Parfum",
    // Eau de Parfum, is not I Want Choo); with no field, or one that says
    // the same, it may be either, and the product decides: one that keeps
    // the word in its name, or one whose concentration it is, is the
    // perfume. A full phrase ("Sauvage Eau de Parfum") is always the
    // concentration, and a field that says otherwise wins. A product whose
    // name goes on where the label's ends ("Le Beau Le Parfum" for "Le
    // Beau") is a flanker, the closest.
    var field = _concKey(ident.concentration), phrase = name.conc, conc = field;
    if (phrase && field && field !== phrase) {
      if (name.bare ? shop.phrase !== phrase : !!shop.phrase) return 1;
    } else if (phrase) {
      if (shop.phrase === phrase || (!shop.phrase && shop.conc === phrase)) conc = '';
      else return 1;
    } else if (shop.phrase) {
      return 1;
    }
    if (conc && shop.conc && conc !== shop.conc) return 1;
    if ((name.intense || _isIntense(ident.concentration)) !== shop.intense) return 1;
    if (_isSet(product, shop.words) !== labelIsSet) return 1;
    return 2;
  }
  // The shop's candidates that answer the label, best first: the fit, then
  // the label's kind (bottle or set) and concentration, then availability,
  // then the shop's order.
  function _rankByLabel(ident, products) {
    var name = _labelName(ident && ident.name, _labelWords(ident && ident.brand));
    var labelIsSet = name.words.some(function(w) { return _SET_WORDS.test(w); });
    var conc = _concKey(ident && ident.concentration) || name.conc;
    var out = [];
    (products || []).forEach(function(p, i) {
      var fit = _labelFit(ident || {}, p);
      if (!fit) return;
      var shop = _productName(p);
      var isSet = _isSet(p, shop.words);
      out.push({ p: p, k: [fit, isSet === labelIsSet ? 1 : 0, (!conc || !shop.conc || shop.conc === conc) ? 1 : 0, p.available === false ? 0 : 1, -i] });
    });
    out.sort(function(a, b) {
      for (var j = 0; j < 5; j++) if (a.k[j] !== b.k[j]) return b.k[j] - a.k[j];
      return 0;
    });
    return out.map(function(o) { return o.p; });
  }

  function _empty(node) {
    while (node && node.firstChild) node.removeChild(node.firstChild);
  }
  function _now() {
    return (typeof performance !== 'undefined' && performance && typeof performance.now === 'function') ? performance.now() : Date.now();
  }
  function _raf(fn) {
    if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(fn);
    return setTimeout(fn, 16);
  }
  function _focus(node) {
    if (!node || typeof node.focus !== 'function') return;
    try { node.focus({ preventScroll: true }); } catch (e) { try { node.focus(); } catch (e2) {} }
  }
  function _buzz(pattern) {
    try { if (navigator.vibrate) navigator.vibrate(pattern); } catch (e) {}
  }
  function _intel(name, data) {
    var intel = window.PriveBrainIntel;
    if (!intel || typeof intel._track !== 'function') return;
    try { intel._track(name, data || {}); } catch (e) {}
  }
  // Runs `fn` once the transition of `prop` on `node` ends, or after `ms`
  // plus a margin if the event never comes (reduced motion, display: none).
  function _onceTransition(node, prop, ms, fn) {
    var done = false;
    function go() { if (done) return; done = true; node.removeEventListener('transitionend', h); fn(); }
    function h(e) { if (e && e.target === node && (!prop || e.propertyName === prop)) go(); }
    node.addEventListener('transitionend', h);
    setTimeout(go, ms + 80);
  }

  // Line icons drawn to the 24-unit square ink box with a 1.75 stroke
  // (DESIGN.md, The Square Glyph Rule). Everything is path data so one
  // builder serves all of them.
  var _BISD_ICONS = {
    close: ['M4 4l16 16', 'M20 4 4 20'],
    info: ['M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0', 'M12 11v5', 'M12 8h.01'],
    camera: ['M3 8h3.4l1.8-3h7.6l1.8 3H21v13H3Z', 'M7.5 14a4.5 4.5 0 1 0 9 0a4.5 4.5 0 1 0-9 0'],
    cameraOff: ['M3 8h3.4l1.8-3h7.6l1.8 3H21v13H3Z', 'm3 3 18 18'],
    gallery: ['M3 3h18v18H3Z', 'M6.7 8.5a1.8 1.8 0 1 0 3.6 0a1.8 1.8 0 1 0-3.6 0', 'm21 15-5-5-6.5 6.5-2.5-2.5L3 18'],
    search: ['M3 10.5a7.5 7.5 0 1 0 15 0a7.5 7.5 0 1 0-15 0', 'm21 21-5.2-5.2'],
    cart: ['M3 8h18l-1.6 13H4.6Z', 'M8.5 8V6a3.5 3.5 0 0 1 7 0v2'],
    check: ['m4 12.5 5.5 5.5L20 6'],
    retry: ['M21 12a9 9 0 1 1-2.6-6.4', 'M21 3v6h-6'],
    chevron: ['m6 9 6 6 6-6'],
    chevronUp: ['m6 15 6-6 6 6'],
    shield: ['M12 2.2 21.8 5.6v6.6c0 5.4-3.9 9.5-9.8 9.6-5.9-.1-9.8-4.2-9.8-9.6V5.6Z', 'm8.2 11.6 2.7 2.7 4.9-5.7'],
    store: ['M3 9 5 3h14l2 6', 'M3 9h18v2.5a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0Z', 'M4.5 13.5V21h15v-7.5', 'M10 21v-5h4v5'],
    lock: ['M3 10h18v11H3Z', 'M7.5 10V7a4.5 4.5 0 0 1 9 0v3'],
    tag: ['M3 13V3h10l8 8-10 10Z', 'M6 7.5a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0-3 0'],
    truck: ['M3 4h11v12H3Z', 'M14 9h4.5L21 12v4h-7', 'M5 19a2 2 0 1 0 4 0a2 2 0 1 0-4 0', 'M15 19a2 2 0 1 0 4 0a2 2 0 1 0-4 0'],
    heat: ['M8.4 3.1v10.4a3.6 3.6 0 1 1-4 0V3.1a2 2 0 0 1 4 0Z', 'M15.6 6.6c1.5 1.4 1.5 3 0 4.4s-1.5 3 0 4.4', 'M20.4 6.6c1.5 1.4 1.5 3 0 4.4s-1.5 3 0 4.4'],
    cool: ['M12 2.8v18.4M2.8 12h18.4', 'M19.4 15.6 15.8 12l3.6-3.6', 'M4.6 8.4 8.2 12l-3.6 3.6', 'M15.6 4.6 12 8.2 8.4 4.6', 'M8.4 19.4 12 15.8l3.6 3.6'],
    year: ['M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0', 'M12 7v5l3 2'],
    day: ['M8 12a4 4 0 1 0 8 0a4 4 0 1 0-8 0', 'M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12'],
    night: ['M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z'],
    star: ['M12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26Z'],
  };
  // The product page's verdict glyphs keep their own boxes so each lands at
  // the same optical size as on the page (fragrance-data.liquid).
  var _BISD_VIEW = { heat: '1.92 0.22 20.48 20.74', cool: '1.92 1.92 20.15 20.15', year: '2.25 2.25 19.5 19.5', day: '1.25 1.25 21.5 21.5', night: '2.29 2.25 19.46 19.46' };
  var _WA_GLYPH = 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z';

  function _bisdIcon(name, size, attrs) {
    var filled = name === 'whatsapp' || name === 'star';
    var svg = svgEl('svg', {
      viewBox: _BISD_VIEW[name] || '0 0 24 24', width: size || 20, height: size || 20,
      fill: filled ? 'currentColor' : 'none', stroke: filled ? 'none' : 'currentColor',
      'stroke-width': '1.75', 'stroke-linecap': name === 'day' ? 'square' : 'round', 'stroke-linejoin': 'round',
      'aria-hidden': 'true', focusable: 'false', class: 'bisd__icon',
    });
    if (attrs) for (var k in attrs) if (attrs.hasOwnProperty(k)) svg.setAttribute(k, attrs[k]);
    var paths = name === 'whatsapp' ? [_WA_GLYPH] : (_BISD_ICONS[name] || []);
    paths.forEach(function(dd) { svg.appendChild(svgEl('path', { d: dd })); });
    return svg;
  }

  // A button or link with an optional leading icon. `kind` is primary
  // (navy), secondary (warm paper), outline or wa.
  function _bisdButton(tag, kind, icon, label) {
    var b = el(tag, 'bisd__btn bisd__btn--' + kind);
    if (tag === 'button') b.type = 'button';
    if (icon) b.appendChild(_bisdIcon(icon, 18));
    b.appendChild(el('span', null, label));
    return b;
  }
  function _glassCircle(icon, label) {
    var b = el('button', 'bisd__glass bisd__glass--circle');
    b.type = 'button';
    b.setAttribute('aria-label', label);
    b.appendChild(_bisdIcon(icon, 22));
    return b;
  }
  function _srOnly(text) {
    return el('span', 'bisd__sr', text);
  }

  // ── Copy ──
  var _HINT = 'Apunta al frasco o a la caja';
  var _COPY = {
    // One line each: the actions under it are the ways forward, so the line
    // never repeats them (Roque, 2026-10-15).
    nomatch: { title: 'No lo reconocimos', text: 'Prueba con la etiqueta de frente y buena luz.' },
    nomatchIdent: { title: 'No lo encontramos', text: 'Tu foto parece ser %s, pero no lo ubicamos.' },
    down: { title: 'No pudimos buscar', text: 'La búsqueda por foto no responde ahora.' },
    denied: { title: 'Cámara bloqueada', text: 'Tu navegador bloqueó la cámara. Para activarla:', steps: [['iPhone', '«AA» › Configuración del sitio web › Cámara › Permitir'], ['Android', 'Candado › Permisos › Cámara › Permitir']] },
    nocamera: { title: 'La cámara no está disponible', text: 'No pudimos abrir la cámara. Elige una foto del frasco o de la caja, o busca por nombre.' },
  };

  function _buildDrawer() {
    var d = {
      mode: 'idle',
      stream: null,
      inflight: false,
      uploadMode: false,
      lastSource: '',
      lastScrollY: 0,
      matches: [],
      groups: [],
      group: null,
      size: 0,
      activeMatch: 0,
      lastIdent: null,
      photoBlob: null,
      sheetPos: 'peek',
      sheetWant: 'peek',   // the detent last asked for; a collapse to full on a short stage keeps it
      peekPx: 0,
      sheetVisible: false,
      lowLight: false,
      _searchToken: 0,
      _camToken: 0,
      _session: 0,
      _timers: [],
      _busyTimers: [],
      _hintTimers: [],
      _ctl: null,
      _cameraStarting: false,
      _cameraRestarted: false,
      _lumaTimer: null,
      _idleTimer: null,
      _openedAt: 0,
      _inerted: [],
      _locked: false,      // the page behind is fixed and inert (see _lockPage)
      _resultEls: null,
      _stateEls: null,
      _afterClose: null,   // runs once the drawer has closed (a deferred cart-page reload)
    };
    var reduced = (typeof window.matchMedia === 'function') ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

    // ── The DOM, built once ──
    var root = el('div', 'brain-image-search-drawer');
    root.setAttribute('data-open', 'false');
    var backdrop = el('div', 'bisd__backdrop');
    backdrop.addEventListener('click', function() { d.hide(); });
    root.appendChild(backdrop);

    var cam = el('div', 'bisd__cam');
    cam.setAttribute('role', 'dialog');
    cam.setAttribute('aria-modal', 'true');
    cam.setAttribute('aria-label', 'Buscar con la cámara');
    cam.setAttribute('data-mode', 'scanner');
    root.appendChild(cam);

    // The live plane: the picture, the frozen frame, the no-camera glyph,
    // the brackets and the flash.
    var stage = el('div', 'bisd__stage');
    var video = document.createElement('video');
    video.className = 'bisd__video';
    video.setAttribute('autoplay', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    video.setAttribute('aria-hidden', 'true');
    video.muted = true;
    video.hidden = true;
    stage.appendChild(video);
    var still = document.createElement('canvas');
    still.className = 'bisd__still';
    still.setAttribute('role', 'img');
    still.setAttribute('aria-label', 'Tu foto');
    still.hidden = true;
    stage.appendChild(still);
    var nocam = el('div', 'bisd__nocam');
    nocam.hidden = true;
    nocam.appendChild(_bisdIcon('cameraOff', 56, { 'stroke-width': '1.25' }));
    stage.appendChild(nocam);
    var frame = el('div', 'bisd__frame');
    frame.setAttribute('aria-hidden', 'true');
    for (var c = 0; c < 4; c++) frame.appendChild(el('i'));
    stage.appendChild(frame);
    var flash = el('div', 'bisd__flash');
    flash.setAttribute('aria-hidden', 'true');
    stage.appendChild(flash);
    cam.appendChild(stage);
    var scrimTop = el('div', 'bisd__scrim bisd__scrim--top');
    scrimTop.setAttribute('aria-hidden', 'true');
    cam.appendChild(scrimTop);
    var scrimBottom = el('div', 'bisd__scrim bisd__scrim--bottom');
    scrimBottom.setAttribute('aria-hidden', 'true');
    cam.appendChild(scrimBottom);

    // Top row: close · hint · info, which becomes "Otra foto" once there is
    // a photo. The sheet's highest detent rests under this row.
    var top = el('div', 'bisd__top');
    var closeBtn = _glassCircle('close', 'Cerrar la cámara');
    closeBtn.addEventListener('click', function() { d.hide(); });
    top.appendChild(closeBtn);
    var hint = el('p', 'bisd__hint bisd__glass bisd__glass--capsule bisd__glass--sentence', _HINT);
    top.appendChild(hint);
    var slot = el('div', 'bisd__slot');
    var infoBtn = _glassCircle('info', 'Cómo funciona la búsqueda por foto');
    infoBtn.addEventListener('click', function() { d._openInfo(); });
    slot.appendChild(infoBtn);
    var retake = el('button', 'bisd__glass bisd__glass--capsule bisd__glass--label bisd__retake');
    retake.type = 'button';
    retake.appendChild(_bisdIcon('camera', 16));
    retake.appendChild(el('span', null, 'Otra foto'));
    retake.addEventListener('click', function() { d._retakePhoto(); });
    slot.appendChild(retake);
    top.appendChild(slot);
    cam.appendChild(top);

    // Bottom controls: Galería · shutter · Escribir.
    var controls = el('div', 'bisd__controls');
    function sideButton(icon, label) {
      var b = el('button', 'bisd__side');
      b.type = 'button';
      var ring = el('span', 'bisd__glass bisd__glass--circle');
      ring.setAttribute('aria-hidden', 'true');
      ring.appendChild(_bisdIcon(icon, 22));
      b.appendChild(ring);
      b.appendChild(el('span', 'bisd__side-label', label));
      return b;
    }
    var galleryBtn = sideButton('gallery', 'Galería');
    galleryBtn.addEventListener('click', function() { fileInput.click(); });
    controls.appendChild(galleryBtn);
    var shutter = el('button', 'bisd__shutter');
    shutter.type = 'button';
    shutter.setAttribute('aria-label', 'Tomar la foto');
    shutter.appendChild(el('span', 'bisd__shutter-disc'));
    shutter.addEventListener('click', function() { d._shoot(); });
    controls.appendChild(shutter);
    var writeBtn = sideButton('search', 'Escribir');
    writeBtn.addEventListener('click', function() { d._openTextSearch(''); });
    controls.appendChild(writeBtn);
    cam.appendChild(controls);

    // Photo picker (Galería). No `capture`, so the phone offers its library.
    var fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.className = 'bisd__sr';
    fileInput.setAttribute('aria-hidden', 'true');
    fileInput.tabIndex = -1;
    fileInput.addEventListener('change', function() {
      if (fileInput.files && fileInput.files[0]) d._handleFile(fileInput.files[0]);
      fileInput.value = '';
    });
    cam.appendChild(fileInput);

    // The capsule slot above the controls: busy while the search runs, then
    // "Ver resultado" after a swipe-down.
    var busy = el('div', 'bisd__busy bisd__glass bisd__glass--capsule bisd__glass--sentence');
    busy.setAttribute('role', 'status');
    var busyRing = el('span', 'bisd__ring');
    busyRing.setAttribute('aria-hidden', 'true');
    busy.appendChild(busyRing);
    var busyCheck = el('span', 'bisd__busy-check');
    busyCheck.setAttribute('aria-hidden', 'true');
    busyCheck.hidden = true;
    busyCheck.appendChild(_bisdIcon('check', 18, { 'stroke-width': '2' }));
    busy.appendChild(busyCheck);
    var busyText = el('span', 'bisd__busy-text', 'Buscando el perfume…');
    busy.appendChild(busyText);
    var busyCancel = el('button', 'bisd__busy-cancel', 'Cancelar');
    busyCancel.type = 'button';
    busyCancel.hidden = true;
    busyCancel.addEventListener('click', function() { d._cancelSearch(); d._retakePhoto(); });
    busy.appendChild(busyCancel);
    cam.appendChild(busy);
    var peekBtn = el('button', 'bisd__peek bisd__glass bisd__glass--capsule bisd__glass--label');
    peekBtn.type = 'button';
    peekBtn.tabIndex = -1;
    peekBtn.appendChild(_bisdIcon('chevronUp', 16));
    var peekText = el('span', null, 'Ver resultado');
    peekBtn.appendChild(peekText);
    peekBtn.addEventListener('click', function() { d._restoreSheet(); });
    cam.appendChild(peekBtn);

    // The paper plane.
    var sheet = el('section', 'bisd__sheet');
    sheet.hidden = true;
    var grab = el('button', 'bisd__grab');
    grab.type = 'button';
    grab.setAttribute('aria-label', 'Ampliar o reducir el resultado');
    grab.setAttribute('aria-expanded', 'false');
    sheet.appendChild(grab);
    var scroll = el('div', 'bisd__scroll');
    sheet.appendChild(scroll);
    var bar = el('footer', 'bisd__bar');
    bar.hidden = true;
    sheet.appendChild(bar);
    cam.appendChild(sheet);

    var veil = el('div', 'bisd__veil');
    veil.addEventListener('click', function() { d._closeInfo(); });
    cam.appendChild(veil);

    // "Cómo funciona": paper with the same grip and swipe as the result.
    var info = el('div', 'bisd__info');
    info.setAttribute('role', 'dialog');
    info.setAttribute('aria-labelledby', 'bisd-info-title');
    info.hidden = true;
    var infoGrab = el('button', 'bisd__grab');
    infoGrab.type = 'button';
    infoGrab.setAttribute('aria-label', 'Cerrar');
    info.appendChild(infoGrab);
    var infoBody = el('div', 'bisd__info-body');
    var infoTitle = el('h2', 'bisd__info-title', 'Cómo funciona');
    infoTitle.id = 'bisd-info-title';
    infoTitle.tabIndex = -1;
    infoBody.appendChild(infoTitle);
    infoBody.appendChild(el('p', null, 'Toma una foto del frasco o de la caja. Buscamos el producto en el catálogo de Privé y te mostramos su ficha, precio y disponibilidad.'));
    infoBody.appendChild(el('p', null, 'La foto sirve para encontrar el producto; no confirma si un frasco es original. Todo lo que vende Privé es original.'));
    infoBody.appendChild(el('p', null, 'Si no lo reconocemos, puedes buscar por nombre o consultarnos por WhatsApp.'));
    var infoOk = _bisdButton('button', 'secondary', null, 'Entendido');
    infoOk.addEventListener('click', function() { d._closeInfo(); });
    infoBody.appendChild(infoOk);
    info.appendChild(infoBody);
    cam.appendChild(info);

    var toast = el('div', 'bisd__toast');
    toast.setAttribute('aria-hidden', 'true');
    cam.appendChild(toast);
    var live = el('div', 'bisd__sr');
    live.setAttribute('aria-live', 'polite');
    cam.appendChild(live);

    document.body.appendChild(root);
    d.root = root;
    d.cam = cam;
    d.sheet = sheet;
    d.fileInput = fileInput;
    d.galleryInput = fileInput;
    d._els = { stage: stage, video: video, still: still, nocam: nocam, frame: frame, flash: flash, top: top, hint: hint, infoBtn: infoBtn, retake: retake, controls: controls, shutter: shutter, busy: busy, busyText: busyText, busyRing: busyRing, busyCheck: busyCheck, busyCancel: busyCancel, peekBtn: peekBtn, peekText: peekText, grab: grab, scroll: scroll, bar: bar, veil: veil, info: info, infoGrab: infoGrab, infoTitle: infoTitle, toast: toast, live: live };

    // ── Small helpers ──
    d._t = function(ms, fn) { var id = setTimeout(fn, ms); d._timers.push(id); return id; };
    d._clearTimers = function() { d._timers.forEach(clearTimeout); d._timers = []; };
    // Everything that could still fire: used on close and by the tests.
    d._clearAll = function() {
      d._clearTimers(); d._stopHint(); d._stopLuma();
      d._busyTimers.forEach(clearTimeout); d._busyTimers = [];
      clearTimeout(d._idleTimer); clearTimeout(d._toastTimer);
    };
    d._announce = function(text) {
      live.textContent = '';
      setTimeout(function() { live.textContent = text; }, 30);
    };
    d._toast = function(text, icon) {
      _empty(toast);
      if (icon) toast.appendChild(_bisdIcon(icon, 18));
      toast.appendChild(el('span', null, text));
      toast.classList.add('is-on');
      clearTimeout(d._toastTimer);
      d._toastTimer = setTimeout(function() { toast.classList.remove('is-on'); }, 2400);
    };
    d._setMode = function(mode) {
      d.mode = mode;
      cam.setAttribute('data-mode', mode);
    };
    d._isOpen = function() { return root.getAttribute('data-open') === 'true'; };
    d._linkCustomer = function() {
      var meta = document.querySelector('meta[name="prive-cid"]');
      var cid = meta && meta.content;
      if (cid && window.PriveBrainIntel && typeof window.PriveBrainIntel.linkCustomer === 'function') {
        try { window.PriveBrainIntel.linkCustomer(String(cid)); } catch (e) {}
      }
    };
    // The page behind the camera is out of reach for focus and readers
    // while it is open (`inert` where the browser has it; aria-modal and the
    // focus trap cover the rest).
    d._inertPage = function(on) {
      if (typeof HTMLElement === 'undefined' || !('inert' in HTMLElement.prototype)) return;
      if (on) {
        d._inerted = [];
        var kids = document.body.children || [];
        for (var i = 0; i < kids.length; i++) {
          var k = kids[i];
          if (k === root || k.inert) continue;
          k.inert = true;
          d._inerted.push(k);
        }
      } else {
        d._inerted.forEach(function(k) { k.inert = false; });
        d._inerted = [];
      }
    };

    // ── Open / close ──
    // The page behind locks (a fixed body, kept at its scroll position) and
    // goes inert once the drawer has arrived, and is freed once it has gone:
    // the fixed body lays the whole page out again and inert restyles it,
    // which costs a phone a good part of a second, and on the first frame
    // of the travel that work would stall it.
    d._lockPage = function() {
      if (d._locked) return;
      d._locked = true;
      // Where the page is now, not where it was at the tap: a pan or a
      // wheel during the travel moves it, and the lock holds that place.
      d.lastScrollY = window.scrollY || document.documentElement.scrollTop || 0;
      document.documentElement.classList.add('bisd-locked');
      document.body.style.top = '-' + d.lastScrollY + 'px';
      d._inertPage(true);
    };
    d._unlockPage = function() {
      if (!d._locked) return;
      d._locked = false;
      d._inertPage(false);
      document.documentElement.classList.remove('bisd-locked');
      document.body.style.top = '';
      window.scrollTo(0, d.lastScrollY);
    };
    d.show = function() {
      if (d._isOpen()) return;
      var reopening = cam.classList.contains('is-closing');
      if (!reopening) d._returnFocus = document.activeElement || null;
      d._openedAt = _now();
      d._session++;   // work started in an earlier session (an add to cart) must not close this one
      d._cameraRestarted = false;   // each session gets its own restart after a dropped track
      root.setAttribute('data-open', 'true');
      cam.classList.remove('is-closing');
      void cam.offsetHeight;
      cam.classList.add('is-open');
      backdrop.classList.add('is-on');
      // A reopen during the close finds the page still locked.
      _onceTransition(cam, 'transform', reduced.matches ? 0 : 320, function() { if (d._isOpen()) d._lockPage(); });
      document.addEventListener('keydown', d._onKey);
      if (!d._preconnected && document.head) {
        d._preconnected = true;
        var link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = API;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      }
      // A reopen during the close animation finds the stream still running
      // (hide() stops it only once the transition ends): it is resumed where
      // it was, paused since the capture, instead of asked for again.
      var resumed = reopening && !!d.stream;
      d._resetToScanner(true);
      d._linkCustomer();
      if (resumed) d._resumeCamera();
      else d._startCamera();   // synchronously, inside the tap: the permission prompt belongs to the gesture
      _intel('camera_open', { page: String(window.location && window.location.pathname || '') });
      d._t(60, function() { _focus(shutter); });
    };
    d.hide = function() {
      if (!d._isOpen()) return;
      d._cancelSearch(); d._stopHint(); d._clearTimers(); clearTimeout(d._idleTimer);
      _intel('camera_close', { mode: d.mode, dwell_ms: Math.round(_now() - d._openedAt) });
      root.setAttribute('data-open', 'closing');
      cam.classList.add('is-closing');
      cam.classList.remove('is-open');
      backdrop.classList.remove('is-on');
      d._closeInfo(true);
      veil.classList.remove('is-on');
      document.removeEventListener('keydown', d._onKey);
      _onceTransition(cam, 'transform', reduced.matches ? 0 : 240, function() {
        if (cam.classList.contains('is-open')) return;   // reopened meanwhile
        root.setAttribute('data-open', 'false');
        d._stopCamera();
        d._clearSheet();
        d._peekOut();
        d._busyOut();
        cam.classList.remove('is-closing', 'is-lifted', 'is-flat');
        d.mode = 'idle';
        d._unlockPage();
        if (d._returnFocus) _focus(d._returnFocus);   // once the page can take it back
        // An add to cart that answered on the cart page after the shopper
        // had moved on waits here to reload the page (see _addToCart).
        if (d._afterClose) { var go = d._afterClose; d._afterClose = null; go(); }
      });
    };
    d._onKey = function(e) {
      if (!e) return;
      if (e.key === 'Escape') {
        if (!info.hidden) d._closeInfo(); else d.hide();
      } else if (e.key === 'Tab') d._trapFocus(e);
    };
    // The sheet's travel, on the sheet and on its bar (see --sheet-y in the CSS).
    d._setSheetY = function(v) { sheet.style.setProperty('--sheet-y', v); bar.style.setProperty('--sheet-y', v); };
    d._clearSheet = function() {
      sheet.hidden = true; sheet.inert = false; bar.hidden = true;
      _empty(scroll); _empty(bar);
      sheet.classList.remove('is-full', 'is-entering', 'is-dragging');
      d._setSheetY('100%');
      d.sheetPos = 'peek'; d.sheetWant = 'peek'; d.sheetVisible = false;
      d._resultEls = null; d._stateEls = null;
    };
    d._resetToScanner = function(first) {
      d._clearTimers();
      d._setMode('scanner');
      cam.classList.remove('is-lifted', 'is-flat');
      d._clearSheet();
      still.hidden = true; still.classList.remove('is-fading');
      d._busyOut(); d._peekOut();
      frame.className = 'bisd__frame'; frame.hidden = false; nocam.hidden = true;
      shutter.disabled = false;
      info.hidden = true; info.classList.remove('is-open'); veil.classList.remove('is-on');
      if (!first && video.srcObject) { var p = video.play(); if (p && p.catch) p.catch(function() {}); }
      d.matches = []; d.groups = []; d.group = null; d.size = 0; d.activeMatch = 0;
      d.lastIdent = null; d.photoBlob = null; d.lastSource = ''; d.uploadMode = false;
      d._startHint();
    };

    // ── Camera lifecycle ──
    d._startCamera = function() {
      var entrance = Math.max(0, 360 - (_now() - d._openedAt));
      nocam.hidden = true;
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        d._t(entrance, function() { if (d.mode === 'scanner') d._showState('nocamera', { message: 'Este navegador no puede abrir la cámara. Elige una foto del frasco o de la caja, o busca por nombre.' }); });
        return;
      }
      // Guard against re-entry: the first call's getUserMedia may still be
      // awaiting the native permission prompt. Firing it again would race
      // and produce a NotReadableError when one stream attaches and the
      // other is rejected.
      if (d.stream || d._cameraStarting) return;   // a running stream keeps its video as it is
      video.hidden = true; video.classList.remove('is-live');
      d._cameraStarting = true;
      var t0 = _now();
      var token = ++d._camToken;
      navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      }).then(function(stream) {
        d._cameraStarting = false;
        // Closed, restarted, or put to sleep (page hidden) while the
        // permission prompt was up: this stream must not attach.
        if (!d._isOpen() || d.stream || token !== d._camToken) {
          stream.getTracks().forEach(function(tr) { tr.stop(); });
          // Sleep invalidated the start and the page is visible again:
          // the visibility handler found the start still pending, so the
          // scanner would stay dark without a fresh start here.
          if (d._isOpen() && !d.stream && d.mode === 'scanner' && !document.hidden && token !== d._camToken) d._startCamera();
          return;
        }
        d.stream = stream;
        video.srcObject = stream;
        video.hidden = false;
        var track = stream.getVideoTracks()[0];
        if (track) track.addEventListener('ended', d._onTrackEnded);
        var p = video.play();
        if (p && p.catch) p.catch(function() {});
        var firstFrame = function() {
          video.removeEventListener('playing', firstFrame);
          video.classList.add('is-live');
          d._cameraRestarted = false;   // a stream that reached frames earns a restart of its own
          if (!still.hidden && d.mode === 'scanner') d._fadeStill();
          var settings = (track && typeof track.getSettings === 'function') ? track.getSettings() : {};
          _intel('camera_ready', { ms: Math.round(_now() - t0), w: video.videoWidth, h: video.videoHeight, facing: settings.facingMode || '' });
        };
        video.addEventListener('playing', firstFrame);
        d._startLuma();
      }).catch(function(err) {
        d._cameraStarting = false;
        if (!d._isOpen()) return;
        // Invalidated while pending (the page was hidden, or the camera was
        // stopped): not this start's error to show. Start over if the
        // scanner is visible again, otherwise the visibility handler will.
        if (token !== d._camToken) {
          if (!d.stream && d.mode === 'scanner' && !document.hidden) d._startCamera();
          return;
        }
        var name = (err && err.name) || '';
        _intel('camera_denied', { kind: name || 'unknown' });
        var kind = (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') ? 'denied' : 'nocamera';
        var message = null;
        if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') message = 'No encontramos una cámara en este dispositivo. Elige una foto del frasco o de la caja, o busca por nombre.';
        else if (name === 'NotReadableError' || name === 'TrackStartError') message = 'Otra aplicación está usando la cámara. Ciérrala y vuelve a intentar, o elige una foto del frasco o de la caja.';
        d._t(Math.max(0, 360 - (_now() - d._openedAt)), function() { if (d.mode === 'scanner') d._showState(kind, { message: message }); });
      });
    };
    d._onTrackEnded = function() {
      if (!d._isOpen()) return;
      d._stopCamera();
      if (!d._cameraRestarted && d.mode === 'scanner') { d._cameraRestarted = true; d._startCamera(); }
      else if (d.mode === 'scanner') d._showState('nocamera');
    };
    // The stream is still running (a retake, or a reopen during the close
    // that never got to stop it): the video shows and plays again, the still
    // fades on it, and the light reading resumes.
    d._resumeCamera = function() {
      nocam.hidden = true; video.hidden = false;
      var p = video.play(); if (p && p.catch) p.catch(function() {});
      d._t(160, function() { d._fadeStill(); });
      d._startLuma();
    };
    d._stopCamera = function() {
      // A start still awaiting the permission prompt must not attach after
      // this; its callback checks the token and stops the late stream.
      d._camToken++;
      d._stopLuma();
      if (d.stream) {
        d.stream.getTracks().forEach(function(tr) { tr.removeEventListener('ended', d._onTrackEnded); tr.stop(); });
        d.stream = null;
      }
      video.srcObject = null; video.hidden = true; video.classList.remove('is-live');
    };
    // The stream sleeps while a result is read: the frozen frame is kept on
    // the still canvas first, so the picture does not change.
    d._sleepCamera = function() {
      if (d.stream && still.hidden && video.classList.contains('is-live')) d._drawStill(video);
      d._stopCamera();   // also cancels a start that is still pending
    };
    d._armIdleStop = function() {
      clearTimeout(d._idleTimer);
      d._idleTimer = setTimeout(function() { if (d.mode !== 'scanner') d._sleepCamera(); }, 30000);
    };
    document.addEventListener('visibilitychange', function() {
      if (!d._isOpen()) return;
      if (document.hidden) d._sleepCamera();
      else if (d.mode === 'scanner' && !d.stream) d._startCamera();
    });
    window.addEventListener('pagehide', function() { if (d._isOpen()) d._sleepCamera(); });
    window.addEventListener('pageshow', function(e) { if (e && e.persisted && d._isOpen() && d.mode === 'scanner' && !d.stream) d._startCamera(); });

    // The hint responds to time and to the scene.
    d._startHint = function() {
      d._stopHint();
      hint.textContent = _HINT; hint.classList.remove('is-quiet'); d.lowLight = false;
      d._hintTimers.push(setTimeout(function() { if (!d.lowLight) hint.classList.add('is-quiet'); }, 4000));
      d._hintTimers.push(setTimeout(function() { if (!d.lowLight && d.mode === 'scanner') { hint.textContent = 'Toca el botón blanco para buscar'; hint.classList.remove('is-quiet'); } }, 12000));
    };
    d._stopHint = function() { d._hintTimers.forEach(clearTimeout); d._hintTimers = []; };
    // The light reading copies a frame out of the camera, which costs the
    // main thread tens of milliseconds on a phone: once every second and a
    // half is soon enough for "Hay poca luz".
    var lumaCanvas = null;
    d._startLuma = function() {
      d._stopLuma();
      d._lumaTimer = setInterval(function() {
        if (d.mode !== 'scanner' || video.readyState < 2) return;
        try {
          if (!lumaCanvas) { lumaCanvas = document.createElement('canvas'); lumaCanvas.width = 16; lumaCanvas.height = 16; }
          var g = lumaCanvas.getContext('2d', { willReadFrequently: true });
          g.drawImage(video, 0, 0, 16, 16);
          var px = g.getImageData(0, 0, 16, 16).data, sum = 0;
          for (var i = 0; i < px.length; i += 4) sum += px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114;
          var dark = sum / 256 < 40;
          if (dark && !d.lowLight) { d.lowLight = true; hint.textContent = 'Hay poca luz'; hint.classList.remove('is-quiet'); }
          else if (!dark && d.lowLight) { d.lowLight = false; hint.textContent = _HINT; hint.classList.add('is-quiet'); }
        } catch (e) { d._stopLuma(); }
      }, 1500);
    };
    d._stopLuma = function() { clearInterval(d._lumaTimer); d._lumaTimer = null; };

    // ── Capture ──
    d._sourceEl = function() { return (!video.hidden && video.readyState >= 2) ? video : null; };
    function srcSize(src) { return { w: src.videoWidth || src.naturalWidth || src.width, h: src.videoHeight || src.naturalHeight || src.height }; }
    // The still is the frame as the shopper saw it: the stage's own size,
    // cover-fitted like the video, drawn in the same tick.
    d._drawStill = function(src) {
      var s = srcSize(src);
      if (!s.w || !s.h) return false;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var W = Math.round(stage.clientWidth * dpr), H = Math.round(stage.clientHeight * dpr);
      if (!W || !H) return false;
      if (still.width !== W || still.height !== H) { still.width = W; still.height = H; }
      var g = still.getContext('2d');
      var scale = Math.max(W / s.w, H / s.h);
      var dw = s.w * scale, dh = s.h * scale;
      g.drawImage(src, (W - dw) / 2, (H - dh) / 2, dw, dh);
      still.classList.remove('is-fading'); still.hidden = false;
      return true;
    };
    d._fadeStill = function() {
      if (still.hidden) return;
      still.classList.add('is-fading');
      d._t(260, function() { still.hidden = true; still.classList.remove('is-fading'); });
    };
    // The upload: the brackets' rectangle with a margin, never the whole
    // frame, and never more than `maxSide` px.
    var upCanvas = null;
    d._uploadBlob = function(src, rect, maxSide, cb) {
      var s = srcSize(src);
      if (!s.w || !s.h) { cb(null); return; }
      var x0 = 0, y0 = 0, x1 = s.w, y1 = s.h;
      if (rect) {
        var sw = stage.clientWidth, sh = stage.clientHeight;
        var scale = Math.max(sw / s.w, sh / s.h);
        var ox = (sw - s.w * scale) / 2, oy = (sh - s.h * scale) / 2;
        var mx = rect.width * 0.2, my = rect.height * 0.2;
        x0 = Math.max(0, (rect.left - mx - ox) / scale); y0 = Math.max(0, (rect.top - my - oy) / scale);
        x1 = Math.min(s.w, (rect.right + mx - ox) / scale); y1 = Math.min(s.h, (rect.bottom + my - oy) / scale);
        if (x1 - x0 < 64 || y1 - y0 < 64) { x0 = 0; y0 = 0; x1 = s.w; y1 = s.h; }
      }
      var cw = x1 - x0, ch = y1 - y0;
      var k = Math.min(1, maxSide / Math.max(cw, ch));
      if (!upCanvas) upCanvas = document.createElement('canvas');
      upCanvas.width = Math.max(1, Math.round(cw * k)); upCanvas.height = Math.max(1, Math.round(ch * k));
      upCanvas.getContext('2d').drawImage(src, x0, y0, cw, ch, 0, 0, upCanvas.width, upCanvas.height);
      upCanvas.toBlob(function(b) { cb(b); }, 'image/jpeg', 0.8);
    };
    d._flashNow = function() {
      try { flash.animate([{ opacity: reduced.matches ? 0.5 : 0.85 }, { opacity: 0 }], { duration: reduced.matches ? 80 : 140, easing: 'ease-out' }); } catch (e) {}
    };
    d._shoot = function() {
      if (d.mode !== 'scanner') return;
      var src = d._sourceEl();
      if (!src) {
        if (!d.stream && !d._cameraStarting) d._startCamera();
        d._toast('La cámara todavía no está lista', 'camera');
        return;
      }
      var t0 = _now();
      var fr = frame.getBoundingClientRect(), st = stage.getBoundingClientRect();
      var rect = { left: fr.left - st.left, top: fr.top - st.top, right: fr.right - st.left, bottom: fr.bottom - st.top, width: fr.width, height: fr.height };
      _buzz(12); d._flashNow(); d._stopHint(); d._stopLuma();
      d._drawStill(video);
      try { video.pause(); } catch (e) {}
      frame.className = 'bisd__frame is-held';
      shutter.disabled = true;
      d._setMode('busy');
      d._busyIn();
      d.uploadMode = false; d.lastSource = 'camera';
      // This capture's generation. Closing the drawer or retaking moves the
      // token on (through _cancelSearch), so a frame that finishes encoding
      // after that goes nowhere: no request leaves for a closed drawer, and
      // nothing old lands on a fresh scanner.
      var token = ++d._searchToken;
      d._uploadBlob(src, rect, 800, function(blob) {
        if (token !== d._searchToken) return;
        if (!blob) { d._captureFailed('No pudimos tomar la foto', 'camera'); return; }
        d.photoBlob = blob;
        _intel('camera_capture', { ms: Math.round(_now() - t0), bytes: blob.size, w: upCanvas.width, h: upCanvas.height, source: 'camera' });
        d._runSearch(token, function() { return d._request(blob, 1); }, 'camera');
      });
    };
    d._captureFailed = function(text, icon) {
      d._toast(text, icon);
      d._busyOut(); frame.className = 'bisd__frame'; shutter.disabled = false;
      d._setMode('scanner');
      if (video.srcObject) { var p = video.play(); if (p && p.catch) p.catch(function() {}); d._fadeStill(); }
      d._startHint();
    };
    d._handleFile = function(file) {
      if (!file) return;
      if (!/^image\//.test(file.type || '')) { d._toast('Elige una imagen', 'gallery'); return; }
      var t0 = _now();
      d._closeInfo(true);
      // This photo's generation (see _shoot): decoding and encoding are both
      // asynchronous, and the drawer can close while they run.
      var token = ++d._searchToken;
      function dead() { return token !== d._searchToken; }
      function fail() { if (dead()) return; d._captureFailed('No pudimos leer esa foto', 'gallery'); }
      function go(src) {
        if (dead()) { if (src && src.close) { try { src.close(); } catch (e) {} } return; }
        if (d.mode === 'state') { d._hideSheet(false); cam.classList.remove('is-flat'); nocam.hidden = true; frame.hidden = false; }
        if (d.mode === 'result') d._hideSheet(false);
        cam.classList.remove('is-lifted');
        d._stopHint(); d._stopLuma();
        if (!d._drawStill(src)) { fail(); return; }
        if (video.classList.contains('is-live')) { try { video.pause(); } catch (e) {} }
        frame.className = 'bisd__frame is-held'; shutter.disabled = true;
        d._setMode('busy'); d._busyIn();
        d.uploadMode = true; d.lastSource = 'gallery';
        d._uploadBlob(src, null, 1024, function(blob) {
          if (src.close) { try { src.close(); } catch (e) {} }
          if (dead()) return;
          if (!blob) { fail(); return; }
          d.photoBlob = blob;
          _intel('camera_capture', { ms: Math.round(_now() - t0), bytes: blob.size, source: 'gallery' });
          d._runSearch(token, function() { return d._request(blob, 1); }, 'gallery');
        });
      }
      function loadImg() {
        var url;
        try { url = URL.createObjectURL(file); } catch (e) { fail(); return; }
        var img = new Image();
        img.onload = function() { go(img); URL.revokeObjectURL(url); };
        img.onerror = function() { URL.revokeObjectURL(url); fail(); };
        img.src = url;
      }
      if (typeof createImageBitmap === 'function') {
        var made;
        try { made = createImageBitmap(file, { imageOrientation: 'from-image' }); } catch (e) { made = null; }
        if (made && made.then) { made.then(go).catch(loadImg); return; }
      }
      loadImg();
    };

    // ── Search ──
    d._busyIn = function() {
      d._busyTimers.forEach(clearTimeout); d._busyTimers = [];
      busyRing.hidden = false; busyCheck.hidden = true;
      busyText.textContent = 'Buscando el perfume…'; busyText.classList.remove('is-swapping');
      busyCancel.hidden = true; busyCancel.classList.remove('is-in');
      busy.classList.add('is-in');
      d._announce('Buscando el perfume');
      d._busyTimers.push(setTimeout(function() { d._swapBusy('Un momento más…'); }, 5000));
      d._busyTimers.push(setTimeout(function() { d._swapBusy('Sigue tardando'); busyCancel.hidden = false; _raf(function() { busyCancel.classList.add('is-in'); }); }, 12000));
    };
    d._swapBusy = function(text, icon) {
      busyText.classList.add('is-swapping');
      setTimeout(function() {
        busyText.textContent = text;
        if (icon === 'check') { busyRing.hidden = true; busyCheck.hidden = false; }
        busyText.classList.remove('is-swapping');
      }, reduced.matches ? 0 : 160);
    };
    d._busyOut = function() {
      d._busyTimers.forEach(clearTimeout); d._busyTimers = [];
      busy.classList.remove('is-in'); busyCancel.classList.remove('is-in');
    };
    d._cancelSearch = function() {
      d._searchToken++;
      d.inflight = false;
      if (d._ctl) { try { d._ctl.abort(); } catch (e) {} d._ctl = null; }
      d._busyOut();
    };
    d._search = function(blob, source) {
      var token = ++d._searchToken;
      d._runSearch(token, function() { return d._request(blob, 1); }, source);
    };
    // Runs one request (a function returning a promise) and shows the
    // result, the no-match sheet or the service-down sheet.
    d._runSearch = function(token, request, source) {
      d.inflight = true;
      d._lastRequest = request;
      var t0 = _now();
      Promise.resolve().then(request).then(function(data) {
        if (token !== d._searchToken) return;
        d.inflight = false;
        var results = (data && data.results) || [];
        // A result with no title means hydration broke upstream; a blank
        // sheet would be worse than saying the service is down.
        if (results.length && !(results[0] && results[0].title)) throw new Error('hydrate-unavailable');
        _intel('image_search_timing', { client_ms: Math.round(_now() - t0), server_ms: data && data.time_ms, source: source || d.lastSource, results: results.length });
        d._onMatches(data);
      }).catch(function(err) {
        if (token !== d._searchToken) return;
        d.inflight = false;
        var status = err && err.status;
        var text = err && err.message ? String(err.message) : '';
        _intel('image_search_fail', { source: source || d.lastSource, reason: 'error', status: status || text, ms: Math.round(_now() - t0) });
        var msg = null;
        if (status === 429 || text === 'HTTP 429') msg = 'Hubo demasiadas búsquedas seguidas. Espera un momento y vuelve a intentar, o busca por nombre.';
        else if (status === 413 || text === 'HTTP 413') msg = 'La foto es demasiado grande. Prueba con otra, o busca por nombre.';
        d._busyOut(); shutter.disabled = false;
        d._t(160, function() { d._showState('down', { message: msg }); });
      });
    };
    // POST /api/search with the photo. One retry on a network error or a
    // timeout; HTTP errors are final.
    d._request = function(blob, attempt) {
      var fd = new FormData();
      fd.append('image', blob, 'foto.jpg');
      fd.append('limit', String(_maxMatches));
      // The visitor capability lets the server score taste compatibility.
      var capability = _brainCapability();
      if (capability) { fd.append('vid', capability.vid); fd.append('vid_token', capability.vid_token); }
      var ctl = new AbortController();
      d._ctl = ctl;
      var tm = setTimeout(function() { ctl.abort(); }, attempt === 1 ? 12000 : 13000);
      return fetch(API + '/api/search', { method: 'POST', body: fd, signal: ctl.signal }).then(function(r) {
        clearTimeout(tm);
        if (!r.ok) { var e = new Error('HTTP ' + r.status); e.status = r.status; e.name = 'HttpError'; throw e; }
        return r.json();
      }).catch(function(err) {
        clearTimeout(tm);
        if (attempt === 1 && err && (err.name === 'TypeError' || err.name === 'AbortError') && d._ctl === ctl) {
          _intel('image_search_retry', { kind: err.name });
          return d._request(blob, 2);
        }
        throw err;
      });
    };
    d._retrySearch = function() {
      if (!d.photoBlob) { d._retakePhoto(); return; }
      d._hideSheet(false); cam.classList.remove('is-lifted');
      frame.className = 'bisd__frame is-held';
      d._setMode('busy'); d._busyIn();
      d._search(d.photoBlob, 'retry');
    };
    d._onMatches = function(data) {
      var list = (data && data.results) || [];
      d.lastIdent = (data && data.identification) || null;
      d._lastSearchData = data || {};
      // The service reads the label better than it matches the catalogue:
      // an empty answer, or one whose first match is not the perfume on the
      // label (another perfume, a flanker, another concentration, its set),
      // sends the label to the shop's own search first (_labelSearch). What
      // it finds leads; the photo's own matches follow as the candidates.
      var ident = d.lastIdent;
      if (_labelWorthSearching(ident) && (!list.length || _labelFit(ident, list[0]) < 2)) {
        var token = d._searchToken;
        d.inflight = true;
        d._labelSearch(ident).then(function(found) {
          if (token !== d._searchToken) return;
          d.inflight = false;
          if (found.length) {
            d._lastSearchData.found_by = 'label';
            var have = {};
            found.forEach(function(m) { have[m.handle] = true; });
            list = found.concat(list.filter(function(m) { return m && !have[m.handle]; }));
          }
          d._showMatches(list);
        }, function() {
          if (token !== d._searchToken) return;
          d.inflight = false;
          d._showMatches(list);
        });
        return;
      }
      d._showMatches(list);
    };
    d._showMatches = function(list) {
      var data = d._lastSearchData;
      if (!list.length) {
        // Not found: the capsule says so, the brackets let go, then the sheet.
        _intel('image_search_fail', { source: d.lastSource, reason: 'no_match', identification: _identLabel(d.lastIdent) });
        d._swapBusy('No lo reconocimos'); frame.className = 'bisd__frame is-lost';
        d._t(200, function() { d._busyOut(); });
        d._t(260, function() { d._showState('nomatch'); });
        return;
      }
      d.matches = list.slice(0, _maxMatches);
      d.groups = _groupMatches(d.matches);
      // Found: the lock first, the sheet after.
      frame.className = 'bisd__frame is-found';
      _buzz([8, 40, 8]);
      d._swapBusy('Encontrado', 'check');
      d._t(280, function() { d._busyOut(); });
      d._t(420, function() { d._renderResult(0, false); });
      if (window.PriveBrainIntel && typeof window.PriveBrainIntel.trackImageSearch === 'function') {
        try { window.PriveBrainIntel.trackImageSearch(_imageSearchIntelPayload(data, d.matches, d.groups[0].items[0], 'results_shown')); } catch (e) {}
      }
    };
    d._trackImageSearchSelect = function(action) {
      if (!window.PriveBrainIntel || typeof window.PriveBrainIntel.trackImageSearchSelect !== 'function') return;
      try {
        window.PriveBrainIntel.trackImageSearchSelect(_imageSearchIntelPayload(d._lastSearchData || {}, d.matches || [], d.activeMatch || 0, action || 'select'));
      } catch (e) {}
    };

    // ── Ways forward that never depend on Brain ──
    // Escribir: the store's own search drawer, with whatever the photo told
    // us as a starting query. Falls back to the search page.
    // The label to the shop's search, then to the service for hydration.
    // Resolves to hydrated matches, possibly none; the drawer's controller
    // carries the abort, so a cancel, a retake or a close ends it. The
    // shop's predictive search matches every property of a product, its
    // description included, whatever fields it is asked for, and gives at
    // most ten: the titled ones come first, and _rankByLabel keeps only
    // what answers the label. The label goes with its concentration in the
    // shop's token (_labelQuery), which brings the right concentration
    // inside the ten of a large family.
    d._labelSearch = function(ident) {
      var label = _identLabel(ident);
      var q = _labelQuery(ident);
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      // The drawer's cancel: a retake or a close ends every request below.
      var ctl = new AbortController();
      d._ctl = ctl;
      // The shop's answer and its hydration share seven seconds; the
      // product fallback keeps time of its own (_lightMatch).
      var budget = _timedSignal(ctl, 7000);
      var url = root + 'search/suggest.json?q=' + encodeURIComponent(q)
        + '&resources%5Btype%5D=product&resources%5Blimit%5D=10&resources%5Boptions%5D%5Bunavailable_products%5D=last';
      // A throw on the way out (no fetch at all) is a failed label search,
      // never a broken scan: the photo's own answer still shows.
      return Promise.resolve().then(function() {
        return fetch(url, { signal: budget.signal, headers: { Accept: 'application/json' } });
      }).then(function(r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      }).then(function(json) {
        var products = (json && json.resources && json.resources.results && json.resources.results.products) || [];
        var ranked = _rankByLabel(ident, products);
        if (!ranked.length) return [];
        return d._hydrateLabelled(ranked, budget.signal, ctl, root);
      }).then(function(found) {
        budget.stop();
        _intel('image_search_label', { source: d.lastSource, identification: label, results: found.length });
        return found;
      }, function(err) {
        budget.stop();
        throw err;
      });
    };
    // The best candidate's name, in the shop's spelling, to the service's
    // text search; only the shop's candidates come back, in the shop's
    // order. A product the service does not know yet is carried by the
    // shop's own product data, without the dossier. The service's answer
    // runs on the label search's budget; the product request under the
    // drawer's cancel alone, so a service that runs out of time does not
    // take the shop's answer with it.
    d._hydrateLabelled = function(ranked, signal, ctl, root) {
      var top = ranked[0];
      var body = { q: (top.vendor ? top.vendor + ' ' : '') + (_cleanTitle(top.title, top.vendor) || top.title), limit: _maxMatches };
      var capability = _brainCapability();
      if (capability) { body.vid = capability.vid; body.vid_token = capability.vid_token; }
      var wanted = {};
      ranked.forEach(function(p, i) { wanted[p.handle] = i; });
      // A service that fails on the way out, in its status or in its body
      // (a read the budget ends) hydrates nothing; the shop's own product
      // data then carries the best candidate below.
      return fetch(API + '/api/search', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: signal }).then(function(r) {
        return r.ok ? r.json() : null;
      }).catch(function() { return null; }).then(function(json) {
        var found = [];
        ((json && json.results) || []).forEach(function(m) {
          if (!m || !m.handle || !m.title || wanted[m.handle] == null) return;
          for (var i = 0; i < found.length; i++) if (found[i].handle === m.handle) return;
          found.push(m);
        });
        found.sort(function(a, b) { return wanted[a.handle] - wanted[b.handle]; });
        found.forEach(function(m) { m.found_by = 'label'; });
        if (found.length && found[0].handle === top.handle) return found;
        // The service does not know the best candidate yet: the shop's own
        // product data carries it, ahead of what the service did know.
        return d._lightMatch(top, ctl, root).then(function(light) { return light.concat(found); });
      });
    };
    d._lightMatch = function(p, ctl, root) {
      // Five seconds of its own: the budget above may already be spent. The
      // signal stays live through the body read, so a body that stalls ends
      // with the five seconds or the drawer's cancel, as the headers would.
      var own = _timedSignal(ctl, 5000);
      return fetch(root + 'products/' + p.handle + '.js', { signal: own.signal, headers: { Accept: 'application/json' } }).then(function(r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      }).then(function(prod) {
        own.stop();
        var variants = (prod && prod.variants) || [];
        var v = variants.filter(function(x) { return x && x.available; })[0] || variants[0];
        if (!prod || !v) return [];
        var origin = (window.location && window.location.origin) || 'https://priveperfumes.com';
        return [{
          handle: prod.handle, title: prod.title, vendor: prod.vendor, productType: prod.type,
          image: p.image || prod.featured_image || (prod.images && prod.images[0]) || '',
          price: Number(v.price) / 100,
          compareAtPrice: v.compare_at_price ? Number(v.compare_at_price) / 100 : null,
          availableForSale: !!v.available,
          url: origin + root + 'products/' + prod.handle,
          variantIds: ['gid://shopify/ProductVariant/' + v.id],
          profile: null,
          found_by: 'label'
        }];
      }, function() { own.stop(); return []; });
    };
    d._openTextSearch = function(query) {
      var q = String(query || '').trim();
      d.hide();
      var drawer = document.getElementById ? document.getElementById('search-drawer') : null;
      var input = drawer && drawer.querySelector('[name="q"]');
      if (drawer && 'open' in drawer) {
        try {
          drawer.open = true;
          if (input && q) {
            input.value = q;
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
          return;
        } catch (e) {}
      }
      window.location.href = '/search' + (q ? '?q=' + encodeURIComponent(q) + '&type=product' : '');
    };
    // The camera's own WhatsApp when it found nothing to sell. Its link line
    // is the home page, never the page under the camera: a product page's
    // link would read as that product.
    d._whatsAppHelpTarget = function(query) {
      return {
        text: query
          ? 'Hola, busco este perfume: ' + query + '. ¿Lo tienen? Les envío la foto.'
          : 'Hola, busco un perfume que no encontré con la cámara. ¿Me ayudan? Les envío la foto.',
        url: '/',
        desde: 'camara'
      };
    };
    // What the chat opens with for the current result: the product and its
    // link, that it came from the camera, and what the photo looked like when
    // the read named another perfume. A WhatsApp link carries text only, so
    // the photo itself is the shopper's to send.
    d._waTarget = function() {
      var m = d._current() || {};
      var label = _identLabel(d.lastIdent);
      var note = 'Lo encontré con la cámara.';
      if (label && _labelFit(d.lastIdent, m) < 2) note += ' La foto parece ser ' + label + '.';
      return {
        title: m.title || '',
        url: d._productUrl(m),
        variant: _numericId(m.variantIds && m.variantIds[0]),
        desde: 'camara',
        note: note,
        match: m
      };
    };
    d._trackWhatsApp = function(match, handoff, variant) {
      var intel = window.PriveBrainIntel;
      if (!intel || typeof intel.trackWhatsAppTap !== 'function') return;
      var tap = { surface: 'camara', ref: handoff && handoff.ref, variant: variant };
      try {
        if (match) intel.trackWhatsAppTap(match.handle || null, match.title || null, match.vendor || null, match.price != null ? match.price : null, tap);
        else intel.trackWhatsAppTap(null, 'Búsqueda por cámara', null, null, tap);
      } catch (e) {}
    };

    // ── The sheet ──
    function num(v) { return isFinite(v) ? v : 0; }
    d._stageH = function() { return num(cam.clientHeight); };
    // The highest detent rests under the glass row: the chrome is never covered.
    d._fullY = function() {
      var r = top.getBoundingClientRect(), c = cam.getBoundingClientRect();
      return Math.round(num(r.bottom - c.top) + 12);
    };
    d._peekY = function() { return d.peekPx || Math.max(d._fullY(), Math.round(d._stageH() * 0.34)); };
    d._resultPeek = function() { return d._stageH() < 500 ? d._fullY() : Math.max(d._fullY(), Math.round(d._stageH() * 0.34)); };
    // A state sheet is as tall as what it says, like a custom detent.
    d._contentPeek = function() {
      var inner = scroll.firstChild;
      var h = inner ? num(inner.offsetTop) + num(inner.offsetHeight) + 24 : d._stageH();
      return Math.max(d._fullY(), d._stageH() - h);
    };
    d._showSheet = function(peekPx) {
      d.peekPx = peekPx;
      sheet.hidden = false; sheet.inert = false;
      sheet.classList.add('is-entering'); sheet.classList.remove('is-dragging');
      void sheet.offsetHeight;
      scroll.style.setProperty('--bar-h', bar.hidden ? '0px' : num(bar.offsetHeight) + 'px');
      cam.classList.add('is-lifted');
      d.sheetVisible = true; d._peekOut();
      d._snapSheet('peek', true);
      d._t(1200, function() { sheet.classList.remove('is-entering'); });
      d._armIdleStop();
    };
    d._snapSheet = function(pos, animate, dur) {
      // One detent when the peek would rest at the full line (a phone on its
      // side, a state sheet taller than the stage): the sheet opens full, so
      // its content scrolls instead of waiting for a flick that goes nowhere.
      // The position asked for is kept, so the detent comes back once a
      // turn of the phone makes room for it again.
      d.sheetWant = pos;
      if (pos === 'peek' && d._peekY() <= d._fullY()) pos = 'full';
      d.sheetPos = pos;
      sheet.style.setProperty('--snap', dur ? dur + 'ms' : '');
      sheet.classList.toggle('is-dragging', !animate);
      sheet.classList.toggle('is-full', pos === 'full');
      d._setSheetY((pos === 'full' ? d._fullY() : d._peekY()) + 'px');
      if (pos === 'peek') scroll.scrollTop = 0;
      grab.setAttribute('aria-expanded', pos === 'full' ? 'true' : 'false');
      if (d._armPull) d._armPull();
      if (!animate) _raf(function() { sheet.classList.remove('is-dragging'); });
      if (dur) _onceTransition(sheet, 'transform', dur, function() { sheet.style.setProperty('--snap', ''); });
    };
    // The sheet leaves; with keep=true its content stays for "Ver resultado".
    d._hideSheet = function(keep) {
      if (sheet.hidden) return;
      d.sheetVisible = false;
      cam.classList.remove('is-lifted');
      sheet.classList.remove('is-full', 'is-dragging', 'is-entering');
      sheet.style.setProperty('--snap', '400ms');
      d._setSheetY(d._stageH() + 'px');
      if (keep) sheet.inert = true;
      _onceTransition(sheet, 'transform', 400, function() {
        sheet.style.setProperty('--snap', '');
        if (d.sheetVisible) return;
        if (!keep) d._clearSheet();
      });
    };
    d._restoreSheet = function() {
      sheet.inert = false; cam.classList.add('is-lifted');
      d.sheetVisible = true; d._peekOut();
      d._snapSheet('peek', true);
      var h = scroll.querySelector('h2');
      if (h) d._t(560, function() { _focus(h); });
    };
    d._peekIn = function(text) { peekText.textContent = text; peekBtn.classList.add('is-in'); peekBtn.tabIndex = 0; };
    d._peekOut = function() { peekBtn.classList.remove('is-in'); peekBtn.tabIndex = -1; };
    d._dismissSheet = function() {
      if (d.mode !== 'result') { d._snapSheet('peek', true); return; }
      d._hideSheet(true);
      d._peekIn('Ver resultado');
      d._t(420, function() { _focus(peekBtn); });
    };
    (function gestures() {
      var drag = null, swallowClick = false;
      function currentY() {
        try { var m = new DOMMatrixReadOnly(getComputedStyle(sheet).transform); return m.m42; }
        catch (e) { return d.sheetPos === 'full' ? d._fullY() : d._peekY(); }
      }
      function start(y, fromScroll, x) {
        var cur = currentY();   // from wherever the sheet is, mid-flight included
        drag = { y0: y, x0: x || 0, axis: null, s0: cur, last: y, t: _now(), v: 0, fromScroll: fromScroll, moved: false, h: d._stageH(), pk: d._peekY(), fy: d._fullY(), ny: cur };
        sheet.style.setProperty('--snap', ''); d._setSheetY(cur + 'px');
        sheet.classList.add('is-dragging');
      }
      function move(y) {
        if (!drag) return;
        var dy = y - drag.y0;
        var ny = Math.max(drag.fy, Math.min(drag.h, drag.s0 + dy));
        var now = _now();
        var inst = (y - drag.last) / Math.max(1, now - drag.t);
        drag.v = 0.6 * inst + 0.4 * drag.v; drag.last = y; drag.t = now;
        drag.moved = drag.moved || Math.abs(dy) > 4; drag.ny = ny;
        d._setSheetY(ny + 'px');
      }
      function settle(pos, g) {
        var target = pos === 'full' ? g.fy : g.pk;
        var dist = Math.abs(target - g.ny);
        var dur = Math.round(Math.max(180, Math.min(520, dist / Math.max(Math.abs(g.v), 0.9))));
        d._snapSheet(pos, true, dur);
      }
      function end() {
        if (!drag) return;
        var g = drag; drag = null; swallowClick = g.moved && !g.fromScroll;   // a click follows a grip drag, never a content one
        if (!g.moved) { d._snapSheet(d.sheetPos, true); return; }
        var y = g.ny, pk = g.pk, fy = g.fy, one = pk <= fy;   // one detent: a pull down from full leaves
        if (d.sheetPos === 'full' && !one) { settle(g.v > 0.6 || y > fy + (pk - fy) * 0.62 ? 'peek' : 'full', g); return; }   // never dismissed from full
        if (d.mode === 'result' && (g.v > 1.0 || y > pk + (g.h - pk) * 0.4)) { d._dismissSheet(); return; }
        if (g.v < -0.4 || y < pk - (pk - fy) * 0.38) { settle('full', g); return; }
        settle('peek', g);
      }
      grab.addEventListener('pointerdown', function(e) { try { grab.setPointerCapture(e.pointerId); } catch (x) {} start(e.clientY, false); });
      grab.addEventListener('pointermove', function(e) { move(e.clientY); });
      grab.addEventListener('pointerup', end);
      grab.addEventListener('pointercancel', end);
      grab.addEventListener('click', function() {
        if (swallowClick) { swallowClick = false; return; }
        if (d._peekY() <= d._fullY()) { if (d.mode === 'result') d._dismissSheet(); return; }   // one detent: the grip puts a result away
        d._snapSheet(d.sheetPos === 'full' ? 'peek' : 'full', true);
      });
      // In peek the whole sheet is a card you drag; in full, only a pull
      // from the top of the scroll; a pinch is never a drag. The blocking
      // touchmove handler, which has the browser wait on this thread for
      // every move of a gesture, is on the scroll only while such a pull
      // can begin (peek, or full at the top): once the content has
      // scrolled, its flicks wait on nothing. And a touch writes nothing to
      // the sheet until a move has claimed it, so a tap costs no restyle. A
      // drag that loses its finger (a pinch, a pull that comes back above
      // its start) settles on a detent; it never rests where it was left.
      var touch = null, pullArmed = false;
      function atTop() { return !(scroll.scrollTop > 0); }
      function onTouchMove(e) {
        if (!touch) return;
        if (e.touches.length > 1) { touch = null; end(); return; }   // a pinch ends a drag like a lift
        var y = e.touches[0].clientY, dy = y - touch.y0, dx = e.touches[0].clientX - touch.x0;
        if (!drag) {
          // The first move decides: a sideways swipe belongs to the content
          // (the alternatives row scrolls across), an upward one in full to
          // its scroll; a pull down, or any move in peek, takes the sheet.
          if (Math.abs(dx) > Math.abs(dy) || (d.sheetPos === 'full' && (dy <= 0 || !atTop()))) { touch = null; return; }
          start(touch.y0, true, touch.x0);
        } else if (d.sheetPos === 'full' && dy <= 0) { var g = drag; touch = null; drag = null; settle('full', g); return; }   // back above its start: the sheet returns, the move is the scroll's
        if (e.cancelable) e.preventDefault();
        move(y);
      }
      function armPull(on) {
        if (on === pullArmed) return;
        pullArmed = on;
        if (on) scroll.addEventListener('touchmove', onTouchMove, { passive: false });
        else scroll.removeEventListener('touchmove', onTouchMove);
      }
      d._armPull = function() { armPull(d.sheetPos === 'peek' || atTop()); };
      scroll.addEventListener('touchstart', function(e) {
        touch = e.touches.length === 1 && (d.sheetPos === 'peek' || atTop()) ? { y0: e.touches[0].clientY, x0: e.touches[0].clientX } : null;
      }, { passive: true });
      scroll.addEventListener('scroll', d._armPull, { passive: true });
      scroll.addEventListener('touchend', function() { touch = null; end(); });
      scroll.addEventListener('touchcancel', function() { touch = null; end(); });
      d._armPull();
      scroll.addEventListener('pointerdown', function(e) {
        if (e.pointerType === 'mouse' && d.sheetPos === 'peek' && !(e.target.closest && e.target.closest('button, a, summary, input'))) {
          try { scroll.setPointerCapture(e.pointerId); } catch (x) {}
          start(e.clientY, true);
        }
      });
      scroll.addEventListener('pointermove', function(e) { if (e.pointerType === 'mouse') move(e.clientY); });
      scroll.addEventListener('pointerup', function(e) { if (e.pointerType === 'mouse') end(); });
      scroll.addEventListener('wheel', function(e) { if (d.sheetPos === 'peek' && e.deltaY > 8) d._snapSheet('full', true); }, { passive: true });
      // Keyboard: focus landing below the fold opens the sheet instead of
      // scrolling a hidden overflow.
      scroll.addEventListener('focusin', function(e) {
        if (d.sheetPos !== 'peek' || e.target === scroll || !e.target.getBoundingClientRect) return;
        var limit = bar.hidden ? d._stageH() : bar.getBoundingClientRect().top;
        if (e.target.getBoundingClientRect().bottom > limit) d._snapSheet('full', true);
      });
      // The info sheet swipes away the same way.
      var idrag = null;
      infoGrab.addEventListener('pointerdown', function(e) { try { infoGrab.setPointerCapture(e.pointerId); } catch (x) {} idrag = { y0: e.clientY, last: e.clientY, t: _now(), v: 0 }; info.classList.add('is-dragging'); });
      infoGrab.addEventListener('pointermove', function(e) {
        if (!idrag) return;
        var dy = Math.max(0, e.clientY - idrag.y0), now = _now();
        idrag.v = (e.clientY - idrag.last) / Math.max(1, now - idrag.t); idrag.last = e.clientY; idrag.t = now;
        info.style.transform = 'translateY(' + dy + 'px)';
      });
      function iend(e) {
        if (!idrag) return;
        var g = idrag; idrag = null;
        var dy = e.clientY - g.y0;
        info.classList.remove('is-dragging'); info.style.transform = '';
        if (dy > 80 || g.v > 0.6) d._closeInfo();
      }
      infoGrab.addEventListener('pointerup', iend);
      infoGrab.addEventListener('pointercancel', iend);
      infoGrab.addEventListener('click', function() { d._closeInfo(); });
    })();
    window.addEventListener('resize', function() {
      if (sheet.hidden || !d.sheetVisible) return;
      // The detents belong to the stage, so a turn of the phone recomputes
      // them: a result's 34% (or the full line on a short stage) and a state
      // sheet's own height, whose strip above the flat plane moves with it.
      // The sheet then returns to the detent it was asked for, so a collapse
      // to full on the short stage undoes itself on the way back.
      if (d.mode === 'result') d.peekPx = d._resultPeek();
      else if (d.mode === 'state') {
        d.peekPx = d._contentPeek();
        if (cam.classList.contains('is-flat')) nocam.style.setProperty('--strip', d.peekPx + 'px');
      }
      d._snapSheet(d.sheetWant || d.sheetPos, false);
    });

    d._retakePhoto = function() {
      d._cancelSearch(); d._clearTimers(); clearTimeout(d._idleTimer);
      d._hideSheet(false); d._peekOut(); d._busyOut();
      cam.classList.remove('is-flat', 'is-lifted');
      frame.className = 'bisd__frame'; frame.hidden = false; nocam.hidden = true; shutter.disabled = false;
      d.matches = []; d.groups = []; d.group = null; d.size = 0; d.activeMatch = 0; d.lastIdent = null; d.uploadMode = false;
      d._resultEls = null; d._stateEls = null;
      d._setMode('scanner');
      if (d.stream) d._resumeCamera();
      else {
        d._startCamera();   // the still fades on the first live frame
      }
      d._startHint();
      d._t(480, function() { _focus(shutter); });
      d._announce('Cámara lista');
    };
    // Kept for callers of the previous drawer.
    d._backToScanner = d._retakePhoto;
    d._returnToScanner = d._retakePhoto;

    // ── Result ──
    d._groupOf = function(idx) {
      for (var g = 0; g < d.groups.length; g++) {
        if (d.groups[g].items.indexOf(idx) !== -1) return d.groups[g];
      }
      return null;
    };
    d._current = function() { return d.matches[d.activeMatch] || {}; };
    d._renderResult = function(groupIdx, quiet) {
      var g = d.groups[groupIdx];
      if (!g) return;
      d.group = g; d.size = 0; d.activeMatch = g.items[0];
      var main = d.matches[d.activeMatch];
      d._setMode('result');
      frame.className = 'bisd__frame is-found';
      _empty(scroll); _empty(bar);

      // Header: the product's picture on warm paper, vendor in the retail
      // voice, the name in Jost, the identity line under it.
      var head = el('header', 'bisd__head');
      var thumb = el('div', 'bisd__thumb');
      // The picture is the tile's own background, blended inside it: an
      // <img> with mix-blend-mode would have the browser draw the whole
      // sheet through an extra pass on every scrolled frame.
      if (main.image) thumb.style.backgroundImage = _cssUrl(_sizedImage(main.image, 240));
      head.appendChild(thumb);
      var id = el('div', 'bisd__id');
      var vendor = el('p', 'bisd__vendor', main.vendor || '');
      vendor.hidden = !main.vendor;
      id.appendChild(vendor);
      var h2 = el('h2', 'bisd__title', _cleanTitle(main.title, main.vendor) || main.title || '');
      h2.id = 'bisd-result-title';
      h2.tabIndex = -1;
      id.appendChild(h2);
      id.appendChild(d._identityLine(main));
      head.appendChild(id);
      scroll.appendChild(head);

      // What the photo showed, when what we carry is only the closest to it.
      var ident = d.lastIdent;
      var label = _identLabel(ident);
      var mismatch = !!label && _labelFit(ident, main) < 2;
      var seen = null;
      if (mismatch) {
        seen = el('p', 'bisd__seen');
        seen.appendChild(document.createTextNode('Tu foto parece ser '));
        seen.appendChild(el('b', null, label));
        seen.appendChild(document.createTextNode('. Este es lo más parecido en el catálogo.'));
        scroll.appendChild(seen);
      }

      var labels = el('div', 'bisd__labels');
      scroll.appendChild(labels);

      // Sizes of the same perfume: a radio group with one tab stop.
      var sizes = el('div', 'bisd__sizes');
      if (g.items.length > 1) {
        sizes.setAttribute('role', 'radiogroup');
        sizes.setAttribute('aria-label', 'Tamaño');
        g.items.forEach(function(idx, i) {
          var it = d.matches[idx];
          var b = el('button', 'bisd__size');
          b.type = 'button';
          b.setAttribute('role', 'radio');
          b.setAttribute('aria-checked', i === 0 ? 'true' : 'false');
          b.tabIndex = i === 0 ? 0 : -1;
          b.appendChild(el('b', null, it.bottle_size || _bottleSizeFromTitle(it.title) || it.title || ''));
          b.appendChild(el('span', null, _isAvailable(it) ? fmtPrice(it.price) : 'Agotado'));
          if (!_isAvailable(it)) b.classList.add('is-out');
          b.addEventListener('click', function() { d._selectSize(i); });
          sizes.appendChild(b);
        });
        sizes.addEventListener('keydown', function(e) {
          var n = g.items.length, j = d.size, k = e.key;
          if (k === 'ArrowRight' || k === 'ArrowDown') j = (j + 1) % n;
          else if (k === 'ArrowLeft' || k === 'ArrowUp') j = (j - 1 + n) % n;
          else if (k === 'Home') j = 0;
          else if (k === 'End') j = n - 1;
          else return;
          e.preventDefault();
          d._selectSize(j);
          _focus(sizes.children[j]);
        });
        scroll.appendChild(sizes);
      } else sizes.hidden = true;

      // The lead line: honest about what a photo can and cannot tell.
      var lead = el('p', 'bisd__lead');
      lead.appendChild(_bisdIcon('info', 16));
      lead.appendChild(el('span', null, 'La foto sirve para encontrar el producto; no confirma si un frasco es original. Todo lo que vende Privé es original.'));
      scroll.appendChild(lead);

      // The bar: add to cart, and WhatsApp as a glyph square.
      var cart = _bisdButton('button', 'primary', 'cart', 'Agregar al carrito');
      cart.addEventListener('click', function() { d._addToCart(); });
      bar.appendChild(cart);
      var wa = null;
      if (WA_NUMBER) {
        wa = el('a', 'bisd__bar-wa');
        wa.setAttribute('aria-label', 'Consultar por WhatsApp (se abre en una pestaña nueva)');
        wa.target = '_blank';
        wa.rel = 'noopener';
        wa.appendChild(_bisdIcon('whatsapp', 24));
        // The same tap as the product page's button: a stale handoff ticket
        // is renewed before the chat opens, so the chat links back here.
        wa._priveWaBound = true;
        // Tracked with the result shown at the tap: the chat may wait for its
        // ticket while the shopper moves to another candidate.
        wa.addEventListener('click', _whatsAppClick(wa, d._waTarget, function(t, handoff) { d._trackWhatsApp(t.match, handoff, t.variant); }));
        bar.appendChild(wa);
      }
      bar.hidden = false;

      d._resultEls = { head: head, thumb: thumb, vendor: vendor, title: h2, seen: seen, labels: labels, sizes: sizes, cart: cart, wa: wa, more: null, full: null, alts: null, dossier: null };
      d._updateSelection();

      if (quiet) { scroll.scrollTop = 0; d._snapSheet('peek', true); }
      else d._showSheet(d._resultPeek());

      // The rest of the sheet arrives once it has landed: the lift never
      // waits for it.
      var token = d._searchToken;
      var idle = window.requestIdleCallback || function(cb) { return setTimeout(cb, 0); };
      d._t(quiet ? 0 : 560, function() { idle(function() { if (token === d._searchToken && d.group === g) d._renderMore(); }, { timeout: 600 }); });

      d._announce((mismatch ? 'Tu foto parece ser ' + label + '. Lo más parecido: ' : 'Encontramos ') + (main.vendor || '') + ' ' + (_cleanTitle(main.title, main.vendor) || main.title || '') + ', ' + fmtPrice(main.price));
      d._t(quiet ? 100 : 860, function() { _focus(h2); });
    };
    d._productUrl = function(m) { return (m && m.url) || ('https://priveperfumes.com/products/' + ((m && m.handle) || '')); };
    // The identity line under the title, as the product page draws it:
    // familia · concentración · género · año (gender and year share an item).
    d._identityLine = function(m) {
      var p = m.profile || {};
      var wrap = el('div', 'bisd__line');
      var ul = el('ul');
      var items = [], gy = [];
      if (m.category !== 'skincare') {
        items = [p.familia, _concentrationFromTitle(m.title) || p.concentracion || m.concentration].filter(Boolean);
        gy = [_translateGenero(p.genero || m.gender) || _generoFromTitle(m.title), p.releaseYear].filter(Boolean);
      }
      function sep() { var s = el('span', 'bisd__sep', '·'); s.setAttribute('aria-hidden', 'true'); return s; }
      items.forEach(function(tx) { var li = el('li'); li.appendChild(sep()); li.appendChild(document.createTextNode(String(tx))); ul.appendChild(li); });
      if (gy.length) {
        var li2 = el('li');
        li2.appendChild(sep()); li2.appendChild(document.createTextNode(String(gy[0])));
        if (gy.length > 1) { li2.appendChild(_srOnly(', ')); li2.appendChild(sep()); li2.appendChild(document.createTextNode(String(gy[1]))); }
        ul.appendChild(li2);
      }
      wrap.appendChild(ul);
      if (!ul.children.length) wrap.hidden = true;
      return wrap;
    };
    d._selectSize = function(i) {
      var els = d._resultEls;
      if (!els || !d.group || i < 0 || i >= d.group.items.length) return;
      d.size = i; d.activeMatch = d.group.items[i];
      var radios = els.sizes.querySelectorAll('.bisd__size');
      for (var j = 0; j < radios.length; j++) { radios[j].setAttribute('aria-checked', i === j ? 'true' : 'false'); radios[j].tabIndex = i === j ? 0 : -1; }
      d._updateSelection();
    };
    // Kept for callers of the previous drawer: a match index, anywhere in
    // the groups.
    d._setMatch = function(i) {
      if (i < 0 || i >= d.matches.length) return;
      var g = d._groupOf(i);
      if (!g) return;
      if (g !== d.group) { d._renderResult(d.groups.indexOf(g), true); }
      d._selectSize(g.items.indexOf(i));
    };
    d._updateSelection = function() {
      var els = d._resultEls;
      if (!els) return;
      var it = d._current();
      _empty(els.labels);
      var out = !_isAvailable(it);
      var sale = it.compareAtPrice != null && it.price != null && Number(it.compareAtPrice) > Number(it.price);
      if (sale) { var cmp = el('span', 'bisd__compare'); cmp.appendChild(_srOnly('Antes ')); cmp.appendChild(document.createTextNode(fmtPrice(it.compareAtPrice))); els.labels.appendChild(cmp); }
      if (it.price != null) {
        var price = el('span', 'bisd__price' + (sale ? ' is-sale' : ''));
        if (sale) price.appendChild(_srOnly('Ahora '));
        price.appendChild(document.createTextNode(fmtPrice(it.price)));
        els.labels.appendChild(price);
      }
      if (out) els.labels.appendChild(el('span', 'bisd__lbl bisd__lbl--out', 'Agotado'));
      else if (it.inventory != null && it.inventory > 0 && it.inventory < 3) els.labels.appendChild(el('span', 'bisd__lbl bisd__lbl--scarce', 'Últimas unidades'));
      else els.labels.appendChild(el('span', 'bisd__lbl', 'Disponible'));
      var threshold = _freeShippingThreshold();
      if (!out && threshold != null && it.price != null && Number(it.price) >= threshold) els.labels.appendChild(el('span', 'bisd__lbl', 'Envío gratis'));
      if (sale) els.labels.appendChild(el('span', 'bisd__lbl bisd__lbl--markdown', '−' + Math.round((1 - Number(it.price) / Number(it.compareAtPrice)) * 100) + '%'));
      var variantId = _numericId(it.variantIds && it.variantIds[0]);
      // The button follows the selected size, except while an add is in
      // flight or being confirmed: that button belongs to the size it sent.
      if (!els.adding) {
        els.cart.disabled = out || !variantId;
        els.cart.querySelector('span').textContent = out ? 'Agotado' : 'Agregar al carrito';
      }
      if (els.wa) els.wa.href = _whatsAppHref(d._waTarget(), null);
      if (els.full) els.full.href = d._productUrl(it);
    };
    // The sections that arrive once the sheet has landed: the trust strip,
    // the dossier, the other candidates, the link to the full page.
    d._renderMore = function() {
      var els = d._resultEls;
      if (!els || els.more) return;
      var main = d.matches[d.group.items[0]] || {};
      var more = el('div', 'bisd__more');
      more.appendChild(d._trustStrip());
      var dossier = null;
      if (main.category !== 'skincare') dossier = d._dossier(main);
      if (dossier) more.appendChild(dossier);
      var alts = d._alternatives();
      more.appendChild(alts);
      var full = el('a', 'bisd__btn bisd__btn--outline bisd__full');
      full.appendChild(el('span', null, 'Ver la ficha completa'));
      full.href = d._productUrl(d._current());
      full.addEventListener('click', function() { d._trackImageSearchSelect('cta_click'); });
      more.appendChild(full);
      scroll.appendChild(more);
      els.more = more; els.full = full; els.alts = alts; els.dossier = dossier;
    };
    // Same request the product form makes, so the cart drawer, the count
    // and the Brain cart tracking all see an ordinary add. The button
    // carries the confirmation, then the store's own cart opens.
    d._addToCart = function() {
      var els = d._resultEls;
      if (!els || els.cart.disabled) return;
      var it = d._current();
      var variantId = _numericId(it.variantIds && it.variantIds[0]);
      if (!variantId) return;
      var tv = window.themeVariables || {};
      var routes = tv.routes || {};
      var settings = tv.settings || {};
      var addUrl = (routes.cartAddUrl || '/cart/add') + '.js';
      var cartUrl = routes.cartUrl || '/cart';
      var label = els.cart.querySelector('span');
      // The drawer session this add belongs to, and the result that sent it.
      // The shopper can close the camera (and open it again), take another
      // photo or pick another candidate before the answer comes: the cart
      // still refreshes, but the answer only confirms on the result that
      // sent it, never closes a scan it did not start, and leaves the
      // mini-cart shut while the camera is open.
      var session = d._session;
      function live() { return session === d._session && d._isOpen() && d._resultEls === els; }
      var name = _cleanTitle(it.title, it.vendor) || it.title || '';
      var said = 'Agregado al carrito: ' + name;
      els.adding = true;
      els.cart.disabled = true;
      els.cart.setAttribute('aria-busy', 'true');
      label.textContent = 'Agregando…';
      d._trackImageSearchSelect('add_to_cart');
      function restore(message) {
        els.adding = false;
        els.cart.removeAttribute('aria-busy');
        // The size may have changed while the add ran: the button follows
        // the selection again (a sold-out size keeps it disabled).
        if (d._resultEls === els) d._updateSelection();
        if (!message || !d._isOpen()) return;
        // The result that sent the add gets the store's words back with its
        // button; after another photo or candidate the toast names the
        // product instead, since "Intenta de nuevo" would point at nothing.
        d._toast(live() ? message : (name ? 'No se agregó ' + name + ' al carrito' : 'No se agregó al carrito'), 'cart');
      }
      function refreshCart(sections, openMiniCart) {
        fetch(cartUrl + '.js').then(function(r) { return r.json(); }).then(function(cartContent) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cartContent } }));
          cartContent.sections = sections;
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cartContent, openMiniCart: openMiniCart } }));
        }).catch(function() {});
      }
      var fd = new FormData();
      fd.append('id', variantId);
      fd.append('quantity', '1');
      fd.append('sections', 'mini-cart');
      fetch(addUrl, { method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function(r) { return r.json().then(function(j) { return { ok: r.ok, json: j }; }); })
        .then(function(res) {
          if (!res.ok) { restore((res.json && res.json.description) || 'No pudimos agregarlo al carrito. Intenta de nuevo.'); return; }
          var sections = res.json && res.json.sections;
          // Same rule as Focal's product form: a page-type cart, or the cart
          // page itself (the header camera is on every page), reloads the
          // cart; the drawer's mini-cart would leave the page's lines stale.
          // When the shopper has moved on inside the camera (another photo
          // or candidate, a reopened camera), the reload waits for the close.
          if (settings.cartType === 'page' || settings.pageType === 'cart') {
            if (live() || !d._isOpen()) { window.location.href = cartUrl; return; }
            d._afterClose = function() { window.location.href = cartUrl; };
            d._toast('Agregado al carrito', 'cart'); d._announce(said);
            refreshCart(sections, false);
            return;
          }
          if (!live()) {
            // The result that sent the add is gone (the camera closed, another
            // photo, another candidate). An open camera hears about it in a
            // toast; a closed one leaves the store's cart to say so.
            if (d._isOpen()) { d._toast('Agregado al carrito', 'cart'); d._announce(said); }
            refreshCart(sections, settings.cartType === 'drawer' && !d._isOpen());
            return;
          }
          els.cart.removeAttribute('aria-busy');
          var old = els.cart.firstChild;
          els.cart.insertBefore(_bisdIcon('check', 18), old);
          els.cart.removeChild(old);
          label.textContent = 'Agregado · ' + (it.bottle_size || _bottleSizeFromTitle(it.title) || '');
          _buzz(8);
          d._announce(said);
          // A plain timeout: closing the camera clears the drawer's own
          // timers, and the cart must refresh either way.
          setTimeout(function() {
            if (live()) d.hide();
            refreshCart(sections, settings.cartType === 'drawer' && !d._isOpen());
          }, 700);
        })
        .catch(function() { restore('No pudimos agregarlo al carrito. Revisa tu conexión e intenta de nuevo.'); });
    };
    // The product page's own trust strip (theme.css .product-meta__trust-*).
    d._trustStrip = function() {
      var box = el('details', 'bisd__trust');
      var s = el('summary', 'bisd__trust-summary');
      var strip = el('div', 'bisd__trust-strip');
      strip.appendChild(_srOnly('Garantías de compra: '));
      var a = el('span', 'bisd__trust-cell bisd__trust-cell--auth');
      a.appendChild(_bisdIcon('shield', 15)); a.appendChild(el('span', null, '100% original'));
      strip.appendChild(a);
      strip.appendChild(el('span', 'bisd__trust-cell', 'Pago seguro'));
      strip.appendChild(el('span', 'bisd__trust-cell', 'Envío nacional'));
      var tg = el('span', 'bisd__trust-toggle');
      tg.appendChild(_bisdIcon('chevron', 10));
      strip.appendChild(tg);
      s.appendChild(strip);
      box.appendChild(s);
      var ul = el('ul', 'bisd__trust-panel');
      [['store', 'Tienda física desde 2019', ' en Plaza Paseo Próceres, San Pedro Sula. Ven a probar o a recoger.'],
       ['lock', 'Tu dinero está seguro.', ' Paga con tarjeta en línea, por transferencia a la cuenta empresarial de PRIVÉ S. de R.L. o al recoger en tienda. Si tu pedido se cancela antes del envío, te devolvemos tu dinero completo.'],
       ['tag', 'Mismo precio', ' en línea y en tienda.'],
       ['truck', 'Envío a todo Honduras:', ' ciudades principales al día siguiente, resto del país en 2 a 3 días hábiles y zonas remotas en 4 o más. En San Pedro Sula, entrega a domicilio, o recoge en tienda el mismo día y paga al recogerlo.']
      ].forEach(function(r) {
        var li = el('li');
        li.appendChild(_bisdIcon(r[0], 17));
        var p = el('span');
        p.appendChild(el('b', null, r[1]));
        p.appendChild(document.createTextNode(r[2]));
        li.appendChild(p);
        ul.appendChild(li);
      });
      box.appendChild(ul);
      return box;
    };
    function dosBox(title, cls) {
      var b = el('div', 'bisd__dos-box' + (cls ? ' ' + cls : ''));
      b.appendChild(el('h3', 'bisd__dos-title', title));
      return b;
    }
    // The dossier, in the product page's own grammar (fragrance-data.liquid):
    // Comunidad fragancera, the verdict pair, Duración, Notas, Acordes.
    d._dossier = function(match) {
      var p = match.profile;
      var wrap = el('section', 'bisd__dos');
      wrap.setAttribute('aria-label', 'Ficha de la fragancia');
      var taste = match.taste_score;
      if (_isLoggedIn() && taste && Number(taste) >= SIMILAR_PERSONAL_THRESHOLD) {
        var tb = dosBox('Tu gusto', 'bisd__dos-box--taste');
        tb.appendChild(el('p', 'bisd__dos-text', 'Se parece en un ' + Math.round(Number(taste)) + '% a lo que has visto y comprado.'));
        wrap.appendChild(tb);
      }
      if (!p) return wrap.children.length ? wrap : null;
      var rating = Number(p.rating) || 0;
      if (rating > 0) {
        var rb = dosBox('Comunidad fragancera');
        var row = el('div', 'bisd__dos-rating');
        var stars = el('span', 'bisd__dos-stars');
        stars.setAttribute('role', 'img');
        stars.setAttribute('aria-label', rating.toFixed(2) + ' de 5 estrellas');
        var full = Math.floor(rating), frac = Math.round((rating - full) * 10), half = frac >= 3 && frac <= 7;
        if (frac > 7) full++;
        for (var i = 1; i <= 5; i++) {
          var st = _bisdIcon('star', 18);
          if (i <= full) { /* amber, the box's own colour */ }
          else if (i === full + 1 && half) {
            st.setAttribute('class', 'bisd__icon bisd__dos-star--half');
            var ghost = _bisdIcon('star', 18);
            ghost.setAttribute('class', 'bisd__icon bisd__dos-star--off bisd__dos-star--under');
            stars.appendChild(ghost);
          } else st.setAttribute('class', 'bisd__icon bisd__dos-star--off');
          stars.appendChild(st);
        }
        row.appendChild(stars);
        row.appendChild(el('span', 'bisd__dos-score', rating.toFixed(2)));
        var v = _votesText(p.totalVotes);
        if (v) row.appendChild(el('span', 'bisd__dos-votes', v));
        rb.appendChild(row);
        wrap.appendChild(rb);
      }
      // Verdicts: one bordered container, two cells, one soft wall. Raw
      // votes are compared before rounding, as the product page does.
      var pair = el('div', 'bisd__dos-pair');
      var sv = p.seasonVotes || {}, tv = p.timeOfDayVotes || {};
      var clima = _climaVerdict({ fresh: (+sv.spring || 0) + (+sv.summer || 0), warm: (+sv.fall || 0) + (+sv.autumn || 0) + (+sv.winter || 0) });
      if (clima) {
        var cb = dosBox('Ideal para');
        var cv = el('div', 'bisd__dos-verdict');
        cv.appendChild(el('span', null, clima));
        cv.appendChild(_bisdIcon(clima === 'Clima caluroso' ? 'heat' : clima === 'Clima fresco' ? 'cool' : 'year', 20));
        cb.appendChild(cv);
        pair.appendChild(cb);
      }
      var oc = _ocasionVerdict({ dia: +tv.day || 0, noche: +tv.night || 0 });
      if (oc) {
        var ob = dosBox('Ocasión');
        var ov = el('div', 'bisd__dos-verdict');
        ov.appendChild(el('span', null, oc));
        if (oc === 'Día y noche') {
          var pr = el('span', 'bisd__dos-verdict-pair');
          pr.appendChild(_bisdIcon('day', 20)); pr.appendChild(el('span', 'bisd__dos-slash', '/')); pr.appendChild(_bisdIcon('night', 20));
          ov.appendChild(pr);
        } else ov.appendChild(_bisdIcon(oc === 'Día' ? 'day' : 'night', 20));
        ob.appendChild(ov);
        pair.appendChild(ob);
      }
      if (pair.children.length) { if (pair.children.length === 2) pair.classList.add('bisd__dos-pair--split'); wrap.appendChild(pair); }
      var lv = p.longevityVotes || {};
      var tot = 0, sum = 0;
      [['love_it', 5], ['like_it', 4], ['neutral', 3], ['dislike_it', 2], ['hate_it', 1]].forEach(function(k) { var n = +lv[k[0]] || 0; tot += n; sum += n * k[1]; });
      if (tot > 0) {
        var score = Math.max(1, Math.floor(sum / tot));
        var names = ['Poca', 'Regular', 'Buena', 'Larga', 'Excelente'];
        var db = dosBox('Duración');
        var scale = el('div', 'bisd__dur');
        scale.setAttribute('role', 'img');
        scale.setAttribute('aria-label', 'Duración: ' + names[score - 1]);
        names.forEach(function(n, i) {
          var it = el('div', 'bisd__dur-item' + (i + 1 === score ? ' is-active' : ''));
          it.appendChild(el('div', 'bisd__dur-seg'));
          it.appendChild(el('span', 'bisd__dur-label', n));
          scale.appendChild(it);
        });
        db.appendChild(scale);
        wrap.appendChild(db);
      }
      var rows = [['Salida', p.topNotes], ['Corazón', p.heartNotes], ['Fondo', p.baseNotes]].filter(function(r) { return r[1] && r[1].length; });
      if (rows.length) {
        var nb = dosBox('Notas');
        rows.forEach(function(r) {
          var n = el('div', 'bisd__note');
          n.appendChild(el('div', 'bisd__note-label', r[0]));
          var pills = el('div', 'bisd__note-pills');
          r[1].forEach(function(tx) { pills.appendChild(el('span', 'bisd__pill', String(tx))); });
          n.appendChild(pills);
          nb.appendChild(n);
        });
        wrap.appendChild(nb);
      }
      // Acordes: the one section without a card, on half-strength paper
      // under an amber rule.
      if (p.mainAccords && p.mainAccords.length) {
        var ab = el('div', 'bisd__dos-acordes');
        ab.appendChild(el('h3', 'bisd__dos-title', 'Acordes'));
        var chips = el('div', 'bisd__chips');
        p.mainAccords.forEach(function(tx) { chips.appendChild(el('span', 'bisd__chip', String(tx))); });
        ab.appendChild(chips);
        wrap.appendChild(ab);
      }
      return wrap.children.length ? wrap : null;
    };
    // The other candidates, one card per perfume (not per size).
    d._alternatives = function() {
      var sec = el('section', 'bisd__alts');
      sec.setAttribute('aria-label', 'Otras coincidencias');
      var cur = d.group;
      var others = d.groups.filter(function(g) { return g !== cur; }).slice(0, 5);
      sec.appendChild(el('h3', 'bisd__alts-title', '¿No es este?'));
      sec.appendChild(el('p', 'bisd__alts-lead', others.length ? 'Otros productos que se parecen a tu foto:' : 'Toma otra foto o busca por nombre.'));
      if (others.length) {
        var row = el('div', 'bisd__alts-row');
        others.forEach(function(g) {
          var it = d.matches[g.items[0]];
          var b = el('button', 'bisd__alt');
          b.type = 'button';
          var media = el('span', 'bisd__alt-img');
          if (it.image) media.style.backgroundImage = _cssUrl(_sizedImage(it.image, 256));
          b.appendChild(media);
          var name = _cleanTitle(it.title, it.vendor) || it.title || '';
          b.appendChild(el('span', 'bisd__alt-vendor', it.vendor || ''));
          b.appendChild(el('span', 'bisd__alt-name', name + (g.items.length > 1 ? '' : ' · ' + (it.bottle_size || _bottleSizeFromTitle(it.title) || '')).replace(/ · $/, '')));
          if (it.price != null) b.appendChild(el('span', 'bisd__alt-price', fmtPrice(it.price)));
          b.setAttribute('aria-label', ((it.vendor || '') + ' ' + name + (it.price != null ? ', ' + fmtPrice(it.price) : '')).trim());
          b.addEventListener('click', function() {
            d._renderResult(d.groups.indexOf(g), true);
            d._trackImageSearchSelect('alternative');
          });
          row.appendChild(b);
        });
        sec.appendChild(row);
      }
      var acts = el('div', 'bisd__alts-actions');
      var again = _bisdButton('button', 'secondary', 'camera', 'Tomar otra foto');
      again.addEventListener('click', function() { d._retakePhoto(); });
      var type = _bisdButton('button', 'secondary', 'search', 'Buscar por nombre');
      type.addEventListener('click', function() { d._openTextSearch(''); });
      acts.appendChild(again); acts.appendChild(type);
      sec.appendChild(acts);
      return sec;
    };

    // ── States: no match, service down, camera blocked, no camera ──
    d._showState = function(kind, opts) {
      opts = opts || {};
      d._cancelSearch();
      var q = _identLabel(d.lastIdent);
      var c = _COPY[kind === 'nomatch' && q ? 'nomatchIdent' : kind] || _COPY.down;
      var flat = kind === 'denied' || kind === 'nocamera';
      d._setMode('state');
      cam.classList.toggle('is-flat', flat);
      if (flat) { video.hidden = true; still.hidden = true; nocam.hidden = false; frame.hidden = true; }
      else if (kind === 'down') frame.className = 'bisd__frame is-held';
      _empty(scroll); _empty(bar); bar.hidden = true;
      scroll.style.setProperty('--bar-h', '0px');
      var body = el('div', 'bisd__state');
      body.setAttribute('data-kind', kind);
      var h = el('h2', 'bisd__state-title', c.title);
      h.id = 'bisd-state-title';
      h.tabIndex = -1;
      body.appendChild(h);
      var p = el('p', 'bisd__state-text');
      var text = opts.message || c.text;
      if (text.indexOf('%s') !== -1) {
        var parts = text.split('%s');
        p.appendChild(document.createTextNode(parts[0]));
        p.appendChild(el('b', null, q));
        p.appendChild(document.createTextNode(parts[1]));
      } else p.textContent = text;
      body.appendChild(p);
      if (c.steps) {
        var ol = el('ul', 'bisd__state-steps');
        c.steps.forEach(function(s) { var li = el('li'); li.appendChild(el('b', null, s[0])); li.appendChild(document.createTextNode(s[1])); ol.appendChild(li); });
        body.appendChild(ol);
      }
      var acts = el('div', 'bisd__state-actions');
      function btn(kind2, icon, label, fn) { var b = _bisdButton('button', kind2, icon, label); b.addEventListener('click', fn); return b; }
      var canRetryCamera = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
      if (kind === 'nomatch') {
        if (d.lastSource === 'gallery') acts.appendChild(btn('primary', 'gallery', 'Elegir otra foto', function() { fileInput.click(); }));
        else acts.appendChild(btn('primary', 'camera', 'Tomar otra foto', function() { d._retakePhoto(); }));
        acts.appendChild(btn('secondary', 'search', q ? 'Buscar «' + q + '»' : 'Buscar por nombre', function() { d._openTextSearch(q); }));
        if (WA_NUMBER) acts.appendChild(d._waLink(kind));
      } else if (kind === 'down') {
        acts.appendChild(btn('primary', 'search', 'Buscar por nombre', function() { d._openTextSearch(''); }));
        if (WA_NUMBER) acts.appendChild(d._waLink(kind));
        acts.appendChild(btn('outline', 'retry', 'Reintentar', function() { d._retrySearch(); }));
      } else {
        acts.appendChild(btn('primary', 'gallery', 'Elegir una foto', function() { fileInput.click(); }));
        acts.appendChild(btn('secondary', 'search', 'Buscar por nombre', function() { d._openTextSearch(''); }));
        if (canRetryCamera) acts.appendChild(btn('outline', 'retry', 'Reintentar', function() { d._cameraRestarted = false; d._retakePhoto(); }));
      }
      body.appendChild(acts);
      scroll.appendChild(body);
      sheet.hidden = false;
      void sheet.offsetHeight;
      var pk = d._contentPeek();
      if (flat) nocam.style.setProperty('--strip', pk + 'px');
      d._stateEls = { pane: body, kind: kind, title: h };
      d._resultEls = null;
      d._showSheet(pk);
      d._announce(c.title + '. ' + p.textContent);
      d._t(860, function() { _focus(h); });
    };
    d._waLink = function(kind) {
      var a = _bisdButton('a', 'wa', 'whatsapp', 'Consultar por WhatsApp');
      a.appendChild(_srOnly(' (se abre en una pestaña nueva)'));
      a.target = '_blank';
      a.rel = 'noopener';
      var target = d._whatsAppHelpTarget(kind === 'nomatch' ? _identLabel(d.lastIdent) : '');
      a.href = _whatsAppHref(target, null);
      a._priveWaBound = true;
      a.addEventListener('click', _whatsAppClick(a, function() { return target; }, function(t, handoff) { d._trackWhatsApp(null, handoff); }));
      return a;
    };

    // ── Cómo funciona ──
    d._openInfo = function() {
      d._panelOpener = document.activeElement || null;
      info.hidden = false; veil.classList.add('is-on');
      void info.offsetHeight;
      info.classList.add('is-open');
      d._t(reduced.matches ? 0 : 200, function() { _focus(infoTitle); });
    };
    d._closeInfo = function(silent) {
      if (info.hidden) return;
      info.classList.remove('is-open'); veil.classList.remove('is-on');
      var back = d._panelOpener; d._panelOpener = null;
      if (!silent && back && d._isOpen()) _focus(back);
      if (silent) { info.hidden = true; return; }
      _onceTransition(info, 'transform', 260, function() { if (!info.classList.contains('is-open')) info.hidden = true; });
    };

    // ── Focus stays inside the open panel ──
    d._trapFocus = function(e) {
      var scope = !info.hidden ? info : cam;
      var nodes = scope.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"]), summary');
      var f = [];
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        if (n.disabled || n.tabIndex < 0 || (n.closest && (n.closest('[hidden]') || n.closest('[inert]')))) continue;
        if (typeof getComputedStyle === 'function') {
          var hiddenByStyle = false, p = n;
          while (p && p !== scope) { var cs = getComputedStyle(p); if (cs.visibility === 'hidden' || cs.display === 'none') { hiddenByStyle = true; break; } p = p.parentElement; }
          if (hiddenByStyle) continue;
        }
        f.push(n);
      }
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1], active = document.activeElement;
      if (e.shiftKey && (active === first || !scope.contains(active))) { e.preventDefault(); _focus(last); }
      else if (!e.shiftKey && (active === last || !scope.contains(active))) { e.preventDefault(); _focus(first); }
    };

    return d;
  }

  function _ensureDrawer() {
    if (!_drawer) _drawer = _buildDrawer();
    return _drawer;
  }

  // The header renders the control itself (snippets/brain-camera-button.liquid),
  // so it paints with the page instead of arriving with this deferred script:
  // a link to the search page until it is bound here, and a button is built
  // only where a header lacks it. This script is deferred, so the markup is
  // parsed by the time the element upgrades; were it ever run mid-parse, the
  // upgrade would land on an empty element, so that case waits for the parser.
  var BrainImageSearch = class extends HTMLElement {
    connectedCallback() {
      var host = this, btn = host.querySelector('.brain-image-search__btn');
      if (!btn && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() { host.connectedCallback(); }, { once: true });
        return;
      }
      if (!btn) {
        btn = el('button', 'brain-image-search__btn');
        btn.type = 'button';
        btn.setAttribute('aria-label', 'Identificar perfume con la cámara');
        btn.title = 'Identificar perfume con la cámara';
        var cameraSvg = svgEl('svg', { width: '20', height: '20', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', focusable: 'false', 'aria-hidden': 'true' });
        cameraSvg.appendChild(svgEl('path', { d: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z' }));
        cameraSvg.appendChild(svgEl('circle', { cx: '12', cy: '13', r: '4' }));
        btn.appendChild(cameraSvg);
        this.appendChild(btn);
      }
      if (btn._brainBound) return;   // a move in the DOM reconnects the element: one handler
      btn._brainBound = true;
      btn.addEventListener('click', function(e) { if (e && e.preventDefault) e.preventDefault(); _ensureDrawer().show(); });
    }
  };
  customElements.define('brain-image-search', BrainImageSearch);

  // Page-level openers. Any control can carry `data-brain-image-search-open`
  // (the search page's no-results state and its related-results notice do).
  // Such a control ships with `hidden` and is revealed here, so a shopper never
  // meets a camera button that does nothing when this script is absent. One
  // delegated listener also covers controls the facet re-render inserts later.
  function _revealImageSearchOpeners(root) {
    var nodes = (root || document).querySelectorAll('[data-brain-image-search-open][hidden]');
    for (var i = 0; i < nodes.length; i++) nodes[i].removeAttribute('hidden');
  }
  document.addEventListener('click', function(event) {
    var target = event.target;
    var trigger = target && target.closest ? target.closest('[data-brain-image-search-open]') : null;
    if (!trigger) return;
    event.preventDefault();
    _ensureDrawer().show();
  });
  _revealImageSearchOpeners();
  var searchPage = document.querySelector('search-page');
  if (searchPage && typeof MutationObserver === 'function') {
    new MutationObserver(function() { _revealImageSearchOpeners(searchPage); })
      .observe(searchPage, { childList: true, subtree: true });
  }

  // ── WhatsApp Product Inquiry ────────────────────────────────────────

  // The WhatsApp square at the head of the product page's buy row (owner,
  // 2026-10-24, the iOS app's bar). The theme draws the link
  // (snippets/buy-whatsapp.liquid) so it paints with the page; this gives it
  // the handoff ticket and the tap tracking. A page without the link (one
  // cached from before) gets the square drawn here.
  var BrainWhatsApp = class extends HTMLElement {
    connectedCallback() {
      // Moved or re-attached: the link is already bound.
      if (this._bound) return;
      this._bound = true;
      var title = (this.getAttribute('product-title') || '').replace(/[\r\n]/g, ' ').substring(0, 200);
      var url = this.getAttribute('product-url') || window.location.href;

      var link = this.querySelector('a');
      if (!link) {
        link = el('a', 'buy-row__whatsapp-link');
        link.target = '_blank';
        link.rel = 'noopener';
        link.setAttribute('aria-label', 'Consultar por WhatsApp');
        var waSvg = svgEl('svg', {width:'24',height:'24',viewBox:'0 0 24 24',fill:'currentColor','aria-hidden':'true',focusable:'false'});
        waSvg.appendChild(svgEl('path', {d:'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z'}));
        link.appendChild(waSvg);
        this.appendChild(link);
      }
      var element = this;
      var wa = _waBase(link);
      // Read at the tap, so the price is the selected size's (not a static
      // Liquid attribute) even when the chat then waits for its ticket.
      var shown = function() {
        var tapTitle = null, tapVendor = null, tapPrice = null;
        var titleEl = document.querySelector('.product-meta__title');
        var vendorEl = document.querySelector('.product-meta__vendor');
        var priceEl = document.querySelector('.price--large');
        if (titleEl) tapTitle = titleEl.textContent.trim();
        if (vendorEl) tapVendor = vendorEl.textContent.trim();
        if (priceEl) {
          var p = parseFloat(priceEl.textContent.replace(/[^\d.,]/g, '').replace(',', ''));
          if (!isNaN(p)) tapPrice = p;
        }
        return { title: tapTitle, vendor: tapVendor, price: tapPrice };
      };
      var target = function() {
        return { title: title, url: url, variant: _selectedVariantId(element, url), desde: 'producto', wa: wa, shown: shown() };
      };
      link.href = _whatsAppHref(target(), null);
      link._priveWaBound = true;
      var waHandle = (url || '').split('/products/')[1];
      if (waHandle) waHandle = waHandle.split('?')[0];
      // The tap takes its own handoff ticket, renewing a stale one before the
      // chat opens; see _whatsAppClick, which the camera's result shares.
      link.addEventListener('click', _whatsAppClick(link, target, function(t, handoff) {
        var intel = window.PriveBrainIntel;
        if (!intel) return;
        var shownAtTap = t.shown || {};
        // Guarded: a collector that throws (an older cached script without the
        // method, a full localStorage) must not take the tap down with it.
        try {
          intel.trackWhatsAppTap(waHandle, shownAtTap.title, shownAtTap.vendor, shownAtTap.price, {
            surface: 'producto',
            ref: handoff && handoff.ref,
            variant: t.variant
          });
        } catch (e) {}
      }));
    }
  };
  customElements.define('brain-whatsapp-inquiry', BrainWhatsApp);
  _bindPageWhatsApp();

  _installEmptyJudgeMeReviewHider();

  // ── Shopify Storefront Hydration (PRV-254) ────────────────────────
  // After brain returns IDs, call Shopify Storefront API to fetch
  // fresh display data (prices, promos, inventory).

  var BRAIN_NS = (window.PriveBrain = window.PriveBrain || {});

  document.dispatchEvent(new CustomEvent('prive:brain:ready'));
})();
